import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { usePermissions, PERMISSIONS } from '../hooks/usePermissions';
import { useUsers } from '../context/UsersContext';
import { useCheckTypes, useWorkflowStatus } from '../hooks/useMasterData';
import SearchableDropdown from '../components/SearchableDropdown';
import MonthYearPicker from '../components/MonthYearPicker';
import USDateInput from '../components/USDateInput';
import Tooltip from '../components/Tooltip';
import { Info, BookmarkPlus, ChevronDown } from 'lucide-react';
import { getCurrentMonthYear, formatMonthYear, getMonthRange, formatDateRange } from '../utils/dateUtils';
import { filterEmojis } from '../utils/emojiFilter';
import './ChecksDashboard.css';

const ChecksDashboard = () => {
  const { showError, showSuccess } = useNotification();
  const { can } = usePermissions();
  const { users } = useUsers();
  const { data: checkTypes, loading: checkTypesLoading } = useCheckTypes(true);
  const { data: workflowStatuses, loading: workflowStatusesLoading } = useWorkflowStatus(true);
  
  // Check dashboard permission - using CHECK_DASHBOARD_VIEW as the main dashboard permission
  const hasDashboardPermission = can(PERMISSIONS.CHECK_DASHBOARD_VIEW);
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [statusViewMode, setStatusViewMode] = useState('chart'); // 'table' or 'chart' - default to chart
  const [financialViewMode, setFinancialViewMode] = useState('chart'); // 'table' or 'chart' - default to chart
  const [hoveredFinancialCheckType, setHoveredFinancialCheckType] = useState(null);
  const [financialSeriesVisibility, setFinancialSeriesVisibility] = useState({
    postedPercentage: true,
    otherPercentage: true,
    remainingPercentage: true
  });
  const [financialCheckTypeVisibility, setFinancialCheckTypeVisibility] = useState({});
  const [practices, setPractices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);

  // Saved filters for ChecksDashboard sidebar
  const [savedFilters, setSavedFilters] = useState([]);
  const [isLoadingSavedFilters, setIsLoadingSavedFilters] = useState(false);
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState('');
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [saveFilterOrder, setSaveFilterOrder] = useState('');
  const [isSavingFilter, setIsSavingFilter] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    depositDateFrom: '',
    depositDateTo: '',
    receivedDateFrom: '',
    receivedDateTo: '',
    completedDateFrom: '',
    completedDateTo: '',
    practiceCodes: [],
    excludePracticeCodes: [],
    locationCodes: [],
    excludeLocationCodes: [],
    statuses: [],
    excludeStatuses: [],
    assigneeIds: [],
    excludeAssigneeIds: [],
    reporterIds: [],
    excludeReporterIds: [],
    checkTypes: [],
    excludeCheckTypes: [],
    workflowStatuses: [],
    excludeWorkflowStatuses: [],
    checkNumber: '',
    payer: '',
    unknown: undefined
  });

  /** KPI sidebar: remembers Exclude mode when no values selected yet */
  const [filterExcludeUiIntent, setFilterExcludeUiIntent] = useState({});
  
  // Refs for "To" date inputs to auto-open calendar
  const depositDateToRef = useRef(null);
  const receivedDateToRef = useRef(null);
  const completedDateToRef = useRef(null);

  // Month/Year selection
  const currentMonthYear = getCurrentMonthYear();
  const [selectedMonth, setSelectedMonth] = useState(currentMonthYear.month);
  const [selectedYear, setSelectedYear] = useState(currentMonthYear.year);
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const handleMonthYearSelect = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    setShowMonthPicker(false);
  };

  // Ref to prevent duplicate API calls
  const practicesFetchedRef = useRef(false);
  
  const fetchPractices = async () => {
    // Only fetch practices if user has dashboard permission
    if (!hasDashboardPermission) {
      return;
    }
    
    // Prevent duplicate calls
    if (practicesFetchedRef.current) {
      return;
    }
    
    practicesFetchedRef.current = true;
    
    try {
      const response = await api.getAllPractices();
      const practicesList = Array.isArray(response) ? response : (response?.items || []);
      setPractices(practicesList.filter(p => p.isActive !== false));
    } catch (error) {
      console.error('Error fetching practices:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch practices';
      showError(errorMessage);
      practicesFetchedRef.current = false; // Reset on error to allow retry
    }
  };

  const fetchKPIData = useCallback(async () => {
    // Only fetch KPI data if user has dashboard permission
    if (!hasDashboardPermission) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Build request body - use filters if provided, otherwise use month range
      const requestBody = { ...filters };
      
      // Check number (supports wildcards)
      if (requestBody.checkNumber && requestBody.checkNumber.trim() !== '') {
        requestBody.checkNumber = requestBody.checkNumber.trim().toUpperCase();
      }
      
      // Payer (supports wildcards)
      // If user enters text without *, it's exact match
      // If user enters text with *, it's regex/wildcard match
      if (requestBody.payer && requestBody.payer.trim() !== '') {
        requestBody.payer = requestBody.payer.trim();
      }
      
      // Only include unknown if it's explicitly set (not undefined)
      if (requestBody.unknown === undefined || requestBody.unknown === null) {
        delete requestBody.unknown;
      }
      
      // Check if user has set any date filters
      const hasDepositDateFilter = filters.depositDateFrom || filters.depositDateTo;
      const hasReceivedDateFilter = filters.receivedDateFrom || filters.receivedDateTo;
      const hasCompletedDateFilter = filters.completedDateFrom || filters.completedDateTo;
      const hasOtherFilters = (
        (filters.practiceCodes && filters.practiceCodes.length > 0) ||
        (filters.excludePracticeCodes && filters.excludePracticeCodes.length > 0) ||
        (filters.locationCodes && filters.locationCodes.length > 0) ||
        (filters.excludeLocationCodes && filters.excludeLocationCodes.length > 0) ||
        (filters.statuses && filters.statuses.length > 0) ||
        (filters.excludeStatuses && filters.excludeStatuses.length > 0) ||
        (filters.assigneeIds && filters.assigneeIds.length > 0) ||
        (filters.excludeAssigneeIds && filters.excludeAssigneeIds.length > 0) ||
        (filters.reporterIds && filters.reporterIds.length > 0) ||
        (filters.excludeReporterIds && filters.excludeReporterIds.length > 0) ||
        (filters.checkTypes && filters.checkTypes.length > 0) ||
        (filters.excludeCheckTypes && filters.excludeCheckTypes.length > 0) ||
        (filters.workflowStatuses && filters.workflowStatuses.length > 0) ||
        (filters.excludeWorkflowStatuses && filters.excludeWorkflowStatuses.length > 0) ||
        (filters.checkNumber && filters.checkNumber.trim() !== '') ||
        (filters.payer && filters.payer.trim() !== '') ||
        (filters.unknown !== undefined && filters.unknown !== null)
      );
      
      // Only add default deposit date if:
      // 1. No date filters are set at all (neither deposit, received, nor completed)
      // 2. AND no other filters are set
      if (!hasDepositDateFilter && !hasReceivedDateFilter && !hasCompletedDateFilter && !hasOtherFilters) {
        const monthRange = getMonthRange(selectedMonth, selectedYear);
        requestBody.depositDateFrom = monthRange.startDate;
        requestBody.depositDateTo = monthRange.endDate;
      } else {
        // If user selected other date ranges (received/completed), remove deposit date if not explicitly set
        if (!hasDepositDateFilter) {
          delete requestBody.depositDateFrom;
          delete requestBody.depositDateTo;
        }
      }
      
      // Clean up empty values - only include params that user explicitly set
      if (!requestBody.depositDateFrom) delete requestBody.depositDateFrom;
      if (!requestBody.depositDateTo) delete requestBody.depositDateTo;
      if (!requestBody.receivedDateFrom) delete requestBody.receivedDateFrom;
      if (!requestBody.receivedDateTo) delete requestBody.receivedDateTo;
      if (!requestBody.completedDateFrom) delete requestBody.completedDateFrom;
      if (!requestBody.completedDateTo) delete requestBody.completedDateTo;
      if (!requestBody.practiceCodes || requestBody.practiceCodes.length === 0) delete requestBody.practiceCodes;
      if (!requestBody.excludePracticeCodes || requestBody.excludePracticeCodes.length === 0) delete requestBody.excludePracticeCodes;
      if (!requestBody.locationCodes || requestBody.locationCodes.length === 0) delete requestBody.locationCodes;
      if (!requestBody.excludeLocationCodes || requestBody.excludeLocationCodes.length === 0) delete requestBody.excludeLocationCodes;
      if (!requestBody.statuses || requestBody.statuses.length === 0) delete requestBody.statuses;
      if (!requestBody.excludeStatuses || requestBody.excludeStatuses.length === 0) delete requestBody.excludeStatuses;
      if (!requestBody.assigneeIds || requestBody.assigneeIds.length === 0) delete requestBody.assigneeIds;
      if (!requestBody.excludeAssigneeIds || requestBody.excludeAssigneeIds.length === 0) delete requestBody.excludeAssigneeIds;
      if (!requestBody.reporterIds || requestBody.reporterIds.length === 0) delete requestBody.reporterIds;
      if (!requestBody.excludeReporterIds || requestBody.excludeReporterIds.length === 0) delete requestBody.excludeReporterIds;
      if (!requestBody.checkTypes || requestBody.checkTypes.length === 0) delete requestBody.checkTypes;
      if (!requestBody.excludeCheckTypes || requestBody.excludeCheckTypes.length === 0) delete requestBody.excludeCheckTypes;
      if (!requestBody.workflowStatuses || requestBody.workflowStatuses.length === 0) delete requestBody.workflowStatuses;
      if (!requestBody.excludeWorkflowStatuses || requestBody.excludeWorkflowStatuses.length === 0) delete requestBody.excludeWorkflowStatuses;
      if (!requestBody.checkNumber || requestBody.checkNumber.trim() === '') delete requestBody.checkNumber;
      if (!requestBody.payer || requestBody.payer.trim() === '') delete requestBody.payer;
      
      const response = await api.getDashboardKPIs(requestBody);
      setKpiData(response);
    } catch (err) {
      console.error('Error fetching KPI data:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to load dashboard data';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedMonth, selectedYear, showError, hasDashboardPermission]);

  useEffect(() => {
    // Only fetch data if user has permission
    if (hasDashboardPermission) {
      fetchPractices();
      fetchKPIData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKPIData, hasDashboardPermission]);

  useEffect(() => {
    fetchKPIData();
  }, [fetchKPIData]);

  // Sync local filters with applied filters when sidebar opens
  useEffect(() => {
    if (showFilterSidebar) {
      setLocalFilters({
        depositDateFrom: filters.depositDateFrom || '',
        depositDateTo: filters.depositDateTo || '',
        receivedDateFrom: filters.receivedDateFrom || '',
        receivedDateTo: filters.receivedDateTo || '',
        completedDateFrom: filters.completedDateFrom || '',
        completedDateTo: filters.completedDateTo || '',
        practiceCodes: filters.practiceCodes || [],
        excludePracticeCodes: filters.excludePracticeCodes || [],
        locationCodes: filters.locationCodes || [],
        excludeLocationCodes: filters.excludeLocationCodes || [],
        statuses: filters.statuses || [],
        excludeStatuses: filters.excludeStatuses || [],
        assigneeIds: filters.assigneeIds || [],
        excludeAssigneeIds: filters.excludeAssigneeIds || [],
        reporterIds: filters.reporterIds || [],
        excludeReporterIds: filters.excludeReporterIds || [],
        checkTypes: filters.checkTypes || [],
        excludeCheckTypes: filters.excludeCheckTypes || [],
        workflowStatuses: filters.workflowStatuses || [],
        excludeWorkflowStatuses: filters.excludeWorkflowStatuses || [],
        checkNumber: filters.checkNumber || '',
        payer: filters.payer || '',
        unknown: filters.unknown !== undefined ? filters.unknown : undefined
      });
      setFilterExcludeUiIntent({});
    }
  }, [showFilterSidebar, filters]);

  // Fetch saved filters when the sidebar opens
  useEffect(() => {
    if (showFilterSidebar) {
      setSelectedSavedFilterId('');
      fetchDashboardSavedFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilterSidebar]);

  const fetchDashboardSavedFilters = async () => {
    setIsLoadingSavedFilters(true);
    try {
      const list = await api.getSavedFilters('CHECK_KPI_DASHBOARD');
      setSavedFilters(Array.isArray(list) ? list : []);
    } catch {
      setSavedFilters([]);
    } finally {
      setIsLoadingSavedFilters(false);
    }
  };

  const handleLoadDashboardSavedFilter = (id) => {
    setSelectedSavedFilterId(id);
    if (!id) return;
    const found = savedFilters.find(f => String(f.id) === String(id));
    if (found && found.payload) {
      setLocalFilters(prev => ({ ...prev, ...found.payload }));
      setFilterExcludeUiIntent({});
    }
  };

  const handleSaveDashboardFilter = async () => {
    if (!saveFilterName.trim()) {
      showError('Please enter a name for the filter.');
      return;
    }
    setIsSavingFilter(true);
    try {
      // Strip any sort-related fields that the backend rejects in payload
      const {
        sortBy: _sortBy,
        sortDirection: _sortDir,
        ...payloadToSave
      } = localFilters;
      const displayOrder = saveFilterOrder !== '' ? parseInt(saveFilterOrder, 10) : 0;
      await api.createSavedFilter({
        type: 'CHECK_KPI_DASHBOARD',
        name: saveFilterName.trim(),
        payload: payloadToSave,
        displayOrder: isNaN(displayOrder) ? 0 : displayOrder
      });
      showSuccess(`Filter "${saveFilterName.trim()}" saved successfully.`);
      setSaveFilterName('');
      setSaveFilterOrder('');
      setShowSaveFilterModal(false);
      fetchDashboardSavedFilters();
    } catch (e) {
      showError(e?.message || e?.data?.message || 'Failed to save filter. Please try again.');
    } finally {
      setIsSavingFilter(false);
    }
  };

  // Fetch locations when practices are selected
  useEffect(() => {
    const selectedPracticeCodes = [...(localFilters.practiceCodes || []), ...(localFilters.excludePracticeCodes || [])];
    if (selectedPracticeCodes.length > 0 && practices.length > 0) {
      const fetchAllLocations = async () => {
        const allLocations = [];
        for (const practiceCode of [...new Set(selectedPracticeCodes)]) {
          try {
            const practice = practices.find(p => p.code === practiceCode);
            if (practice && practice.practiceId) {
              const response = await api.getPracticeLocations(practice.practiceId);
              const locationsList = Array.isArray(response) ? response : (response?.items || []);
              allLocations.push(...locationsList);
            }
          } catch (error) {
            console.error(`Error fetching locations for practice ${practiceCode}:`, error);
            const errorMessage = error?.message || error?.data?.message || `Failed to fetch locations for practice ${practiceCode}`;
            showError(errorMessage);
          }
        }
        setLocations(allLocations);
      };
      fetchAllLocations();
    } else {
      setLocations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters.practiceCodes, localFilters.excludePracticeCodes, practices]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatCompactCurrency = (amount) => {
    const num = Number(amount) || 0;
    const compactValue = new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1
    }).format(Math.abs(num));
    return `${num < 0 ? '-' : ''}$${compactValue}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getSubtitle = () => {
    // Check if user has selected non-deposit date ranges or multiple date ranges
    const hasReceivedDateFilter = filters.receivedDateFrom || filters.receivedDateTo;
    const hasCompletedDateFilter = filters.completedDateFrom || filters.completedDateTo;
    const hasDepositDateFilter = filters.depositDateFrom || filters.depositDateTo;
    const hasOtherFilters = (
      (filters.practiceCodes && filters.practiceCodes.length > 0) ||
      (filters.locationCodes && filters.locationCodes.length > 0) ||
      (filters.statuses && filters.statuses.length > 0) ||
      (filters.assigneeIds && filters.assigneeIds.length > 0) ||
      (filters.reporterIds && filters.reporterIds.length > 0) ||
      (filters.checkTypes && filters.checkTypes.length > 0) ||
      (filters.checkNumber && filters.checkNumber.trim() !== '') ||
      (filters.payer && filters.payer.trim() !== '') ||
      (filters.unknown !== undefined && filters.unknown !== null)
    );
    
    // Count how many date range types are active
    const activeDateRangeCount = [
      hasDepositDateFilter,
      hasReceivedDateFilter,
      hasCompletedDateFilter
    ].filter(Boolean).length;
    
    // If user selected other date ranges (received/completed) or multiple date ranges or other filters,
    // don't show date range in subtitle
    if (hasReceivedDateFilter || hasCompletedDateFilter || activeDateRangeCount > 1 || hasOtherFilters) {
      return 'Overview of payment reconciliation';
    }
    
    if (!kpiData?.scope) {
      // If no data, check if we have filters applied to show relevant subtitle
      if (hasActiveFilters()) {
        return 'Overview of payment reconciliation';
      }
      return 'Overview of payment reconciliation for Current Month';
    }
    const { 
      depositDateFrom, 
      depositDateTo
    } = kpiData.scope;
    
    // Only show deposit date range if it's the only date filter and no other filters
    if (depositDateFrom && depositDateTo && !hasReceivedDateFilter && !hasCompletedDateFilter && !hasOtherFilters) {
      return `Overview of payment reconciliation for Deposit Date Range: ${formatDate(depositDateFrom)} - ${formatDate(depositDateTo)}`;
    }
    
    return 'Overview of payment reconciliation';
  };

  const getDateRangeDisplay = () => {
    // If custom filters are active, show "Custom Range"
    if (hasActiveFilters()) {
      return 'Custom Range';
    }
    // Show month/year format
    return formatMonthYear(selectedMonth, selectedYear);
  };

  // Helper function to calculate max date (3 months from start date)
  const calculateMaxDate = (startDate) => {
    if (!startDate || startDate.trim() === '') return undefined;
    const date = new Date(startDate);
    date.setMonth(date.getMonth() + 3);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleFilterChange = (field, value) => {
    // Filter emojis from string inputs
    const filteredValue = typeof value === 'string' ? filterEmojis(value) : value;
    
    // Check if this is an end date field being set without a start date
    const endDateFields = {
      'depositDateTo': 'depositDateFrom',
      'receivedDateTo': 'receivedDateFrom',
      'completedDateTo': 'completedDateFrom'
    };
    
    // Reverse mapping: start date fields to end date fields (for clearing end date when start date is cleared)
    const startDateFields = {
      'depositDateFrom': 'depositDateTo',
      'receivedDateFrom': 'receivedDateTo',
      'completedDateFrom': 'completedDateTo'
    };
    
    const stringValue = typeof filteredValue === 'string' ? filteredValue : String(filteredValue || '');
    
    // If user is clearing a start date, also clear the corresponding end date
    if (startDateFields[field] && (!stringValue || stringValue.trim() === '')) {
      const endDateField = startDateFields[field];
      setLocalFilters(prev => ({
        ...prev,
        [field]: '',
        [endDateField]: '' // Clear the corresponding end date
      }));
      return; // Exit early after clearing both dates
    }
    
    // If user is trying to set an end date without a start date, show error and prevent
    if (endDateFields[field] && stringValue && stringValue.trim() !== '') {
      const startDateField = endDateFields[field];
      const startDateValue = localFilters[startDateField];
      
      if (!startDateValue || startDateValue.trim() === '') {
        const fieldLabels = {
          'depositDateTo': 'Deposit Date',
          'receivedDateTo': 'Received Date',
          'completedDateTo': 'Completed Date'
        };
        showError(`First you need to select start date for ${fieldLabels[field] || 'the date range'}.`);
        return; // Don't set the value
      }
      
      // Validate 3-month max range
      const startDate = new Date(startDateValue);
      const endDate = new Date(stringValue);
      
      // Calculate difference in months
      const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                         (endDate.getMonth() - startDate.getMonth());
      
      if (monthsDiff > 3) {
        const fieldLabels = {
          'depositDateTo': 'Deposit Date',
          'receivedDateTo': 'Received Date',
          'completedDateTo': 'Completed Date'
        };
        showError(`${fieldLabels[field] || 'Date range'} can only be maximum 3 months.`);
        return; // Don't set the value
      }
    }
    
    setLocalFilters(prev => ({
      ...prev,
      [field]: filteredValue
    }));
    
    // Auto-open corresponding "To" date calendar when "From" date is selected
    if (stringValue && stringValue.trim() !== '') {
      const datePairMap = {
        'depositDateFrom': depositDateToRef,
        'receivedDateFrom': receivedDateToRef,
        'completedDateFrom': completedDateToRef
      };
      
      const toDateRef = datePairMap[field];
      if (toDateRef && toDateRef.current) {
        // Use a small delay to ensure the date value is processed first
        setTimeout(() => {
          if (toDateRef.current && toDateRef.current.openCalendar) {
            toDateRef.current.openCalendar();
          }
        }, 300);
      }
    }
  };

  const [selectedPracticeValue, setSelectedPracticeValue] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const exportToPDF = async () => {
    if (isExporting) return;
    
    try {
      setIsExporting(true);
      
      // Create a temporary container for PDF content
      const printContainer = document.createElement('div');
      printContainer.style.position = 'absolute';
      printContainer.style.left = '-9999px';
      printContainer.style.top = '0';
      printContainer.style.width = '1200px';
      printContainer.style.background = 'white';
      printContainer.style.padding = '40px';
      document.body.appendChild(printContainer);

      // Build PDF content
      let htmlContent = `
        <div style="font-family: Arial, sans-serif; color: #111827;">
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #3b82f6; padding-bottom: 20px;">
            <h1 style="margin: 0 0 10px 0; font-size: 32px; color: #111827;">Checks Dashboard</h1>
            <p style="margin: 0; font-size: 16px; color: #6b7280;">${getSubtitle()}</p>
            <p style="margin: 10px 0 0 0; font-size: 14px; color: #9ca3af;">Generated on ${new Date().toLocaleString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit'
            })}</p>
          </div>
      `;

      // Add active filters if any
      if (hasActiveFilters()) {
        const appliedFilters = getAppliedFilters();
        const pdfExcludeBoxStyle = 'background: #ffe4e6; padding: 10px; border-radius: 6px; border: 1px solid #fda4af;';
        const pdfExcludeStrongStyle = 'color: #9f1239;';
        const pdfIncludeBoxStyle = 'background: white; padding: 10px; border-radius: 6px; border: 1px solid #d1d5db;';
        htmlContent += `
          <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin-bottom: 30px;">
            <h3 style="margin: 0 0 15px 0; font-size: 18px; color: #6b7280;">Active Filters</h3>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;">
        `;
        
        // Date Filters
        if (appliedFilters.depositDateFrom || appliedFilters.depositDateTo) {
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Deposit Date:</strong> ${formatDateRange(appliedFilters.depositDateFrom || '', appliedFilters.depositDateTo || '')}
            </div>
          `;
        }
        
        if (appliedFilters.receivedDateFrom || appliedFilters.receivedDateTo) {
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Received Date:</strong> ${formatDateRange(appliedFilters.receivedDateFrom || '', appliedFilters.receivedDateTo || '')}
            </div>
          `;
        }
        
        if (appliedFilters.completedDateFrom || appliedFilters.completedDateTo) {
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Completed Date:</strong> ${formatDateRange(appliedFilters.completedDateFrom || '', appliedFilters.completedDateTo || '')}
            </div>
          `;
        }
        
        // Practices
        if (appliedFilters.practiceCodes && appliedFilters.practiceCodes.length > 0) {
          const practiceNames = appliedFilters.practiceCodes.map(code => 
            practices.find(p => p.code === code)?.name || code
          ).join(', ');
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Practices:</strong> ${practiceNames}
            </div>
          `;
        }
        
        if (appliedFilters.excludePracticeCodes && appliedFilters.excludePracticeCodes.length > 0) {
          const practiceExcludeText = appliedFilters.excludePracticeCodes.map((code) =>
            `Not ${practices.find(p => p.code === code)?.name || code}`
          ).join(', ');
          htmlContent += `
            <div style="${pdfExcludeBoxStyle}">
              <strong style="${pdfExcludeStrongStyle}">Practice (exclude):</strong> <span style="color: #9f1239;">${practiceExcludeText}</span>
            </div>
          `;
        }
        
        // Locations
        if (appliedFilters.locationCodes && appliedFilters.locationCodes.length > 0) {
          const locationNames = appliedFilters.locationCodes.map(code => 
            locations.find(l => l.code === code)?.name || code
          ).join(', ');
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Locations:</strong> ${locationNames}
            </div>
          `;
        }
        
        if (appliedFilters.excludeLocationCodes && appliedFilters.excludeLocationCodes.length > 0) {
          const locationExcludeText = appliedFilters.excludeLocationCodes.map((code) =>
            `Not ${locations.find(l => l.code === code)?.name || code}`
          ).join(', ');
          htmlContent += `
            <div style="${pdfExcludeBoxStyle}">
              <strong style="${pdfExcludeStrongStyle}">Location (exclude):</strong> <span style="color: #9f1239;">${locationExcludeText}</span>
            </div>
          `;
        }
        
        // Statuses
        const pdfStatusLabels = {
          'NOT_STARTED': 'Not Started',
          'IN_PROGRESS': 'In Progress',
          'UNDER_CLARIFICATIONS': 'Under Clarifications',
          'COMPLETED': 'Completed',
          'OVER_POSTED': 'Over Posted'
        };
        if (appliedFilters.statuses && appliedFilters.statuses.length > 0) {
          const statusNames = appliedFilters.statuses.map(s => pdfStatusLabels[s] || s).join(', ');
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Statuses:</strong> ${statusNames}
            </div>
          `;
        }
        
        if (appliedFilters.excludeStatuses && appliedFilters.excludeStatuses.length > 0) {
          const statusExcludeText = appliedFilters.excludeStatuses.map((s) =>
            `Not ${pdfStatusLabels[s] || s}`
          ).join(', ');
          htmlContent += `
            <div style="${pdfExcludeBoxStyle}">
              <strong style="${pdfExcludeStrongStyle}">Status (exclude):</strong> <span style="color: #9f1239;">${statusExcludeText}</span>
            </div>
          `;
        }
        
        // Assignees
        if (appliedFilters.assigneeIds && appliedFilters.assigneeIds.length > 0) {
          const assigneeNames = appliedFilters.assigneeIds.map(id => {
            const user = users.find(u => (u.userId || u.id) === id);
            return user && user.firstName && user.lastName 
              ? `${user.firstName} ${user.lastName}` 
              : user?.email || id;
          }).join(', ');
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Assignees:</strong> ${assigneeNames}
            </div>
          `;
        }
        
        // Reporters
        if (appliedFilters.reporterIds && appliedFilters.reporterIds.length > 0) {
          const reporterNames = appliedFilters.reporterIds.map(id => {
            const user = users.find(u => (u.userId || u.id) === id);
            return user && user.firstName && user.lastName 
              ? `${user.firstName} ${user.lastName}` 
              : user?.email || id;
          }).join(', ');
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Reporters:</strong> ${reporterNames}
            </div>
          `;
        }
        
        // Check Types
        const pdfCheckTypeLabels = {
          'EFT': 'EFT',
          'ERA': 'ERA',
          'DIT_DRL': 'DIT/DRL',
          'ZERO_PAY': 'Zero Pay',
          'NON_AR': 'NON_AR',
          'REFUND': 'REFUND',
          'LOCK_BOX': 'LOCK_BOX',
          'DEBIT': 'DEBIT',
          'FEE': 'FEE',
          'RBO': 'RBO'
        };
        if (appliedFilters.checkTypes && appliedFilters.checkTypes.length > 0) {
          const checkTypeNames = appliedFilters.checkTypes.map(ct =>
            checkTypes.find(c => c.code === ct)?.name || pdfCheckTypeLabels[ct] || ct
          ).join(', ');
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Check Types:</strong> ${checkTypeNames}
            </div>
          `;
        }
        
        if (appliedFilters.excludeCheckTypes && appliedFilters.excludeCheckTypes.length > 0) {
          const checkTypeExcludeText = appliedFilters.excludeCheckTypes.map((ct) =>
            `Not ${checkTypes.find(c => c.code === ct)?.name || pdfCheckTypeLabels[ct] || ct}`
          ).join(', ');
          htmlContent += `
            <div style="${pdfExcludeBoxStyle}">
              <strong style="${pdfExcludeStrongStyle}">Check Type (exclude):</strong> <span style="color: #9f1239;">${checkTypeExcludeText}</span>
            </div>
          `;
        }
        
        // Workflow Statuses
        if (appliedFilters.workflowStatuses && appliedFilters.workflowStatuses.length > 0) {
          const workflowStatusNames = appliedFilters.workflowStatuses.map(ws => 
            workflowStatuses.find(w => w.code === ws)?.name || ws
          ).join(', ');
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Workflow Statuses:</strong> ${workflowStatusNames}
            </div>
          `;
        }
        
        if (appliedFilters.excludeWorkflowStatuses && appliedFilters.excludeWorkflowStatuses.length > 0) {
          const workflowExcludeText = appliedFilters.excludeWorkflowStatuses.map((ws) =>
            `Not ${workflowStatuses.find(w => w.code === ws)?.name || ws}`
          ).join(', ');
          htmlContent += `
            <div style="${pdfExcludeBoxStyle}">
              <strong style="${pdfExcludeStrongStyle}">Workflow Status (exclude):</strong> <span style="color: #9f1239;">${workflowExcludeText}</span>
            </div>
          `;
        }
        
        // Check Number
        if (appliedFilters.checkNumber) {
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Check Number:</strong> ${appliedFilters.checkNumber}
            </div>
          `;
        }
        
        // Payer
        if (appliedFilters.payer) {
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Payer:</strong> ${appliedFilters.payer}
            </div>
          `;
        }
        
        // Unknown
        if (appliedFilters.unknown !== undefined && appliedFilters.unknown !== null) {
          htmlContent += `
            <div style="${pdfIncludeBoxStyle}">
              <strong style="color: #374151;">Unknown:</strong> ${appliedFilters.unknown === true ? 'True' : 'False'}
            </div>
          `;
        }
        
        htmlContent += `
            </div>
          </div>
        `;
      }

      // Add KPI Cards if available
      if (hasAmountData && amountSummary) {
        htmlContent += `
          <div style="margin-bottom: 30px;">
            <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #111827;">Key Performance Indicators</h3>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
              <div style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border-left: 4px solid #3b82f6; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Total Amount</div>
                <div style="font-size: 20px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.totalAmount)}</div>
                <div style="font-size: 13px; color: #6b7280;">${totalCount || 0} checks</div>
              </div>
              <div style="background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); border-left: 4px solid #10b981; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Posted Amount</div>
                <div style="font-size: 20px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.postedAmount)}</div>
                <div style="font-size: 13px; color: #10b981;">${amountSummary.totalAmount ? `${((amountSummary.postedAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0% of total'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%); border-left: 4px solid #6366f1; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Other Amount</div>
                <div style="font-size: 20px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.otherAmount)}</div>
                <div style="font-size: 13px; color: #6b7280;">${amountSummary.totalAmount ? `${((amountSummary.otherAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0% of total'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); border-left: 4px solid #f59e0b; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Remaining Amount</div>
                <div style="font-size: 20px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.remainingAmount)}</div>
                <div style="font-size: 13px; color: #f59e0b;">${amountSummary.totalAmount ? `${((amountSummary.remainingAmount / amountSummary.totalAmount) * 100).toFixed(1)}% remaining` : '0% remaining'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%); border-left: 4px solid #8b5cf6; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Total Clarification Amount</div>
                <div style="font-size: 20px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.totalClarificationAmount || 0)}</div>
                <div style="font-size: 13px; color: #6b7280;">${amountSummary.totalAmount ? `${((amountSummary.totalClarificationAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total` : '0% of total'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%); border-left: 4px solid #f97316; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Open Clarification Amount</div>
                <div style="font-size: 20px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.openClarificationAmount || 0)}</div>
                <div style="font-size: 13px; color: #f97316;">${amountSummary.openClarificationAmountPercentage != null ? `${amountSummary.openClarificationAmountPercentage.toFixed(1)}% open` : '0% open'}</div>
              </div>
              <div style="background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%); border-left: 4px solid #14b8a6; padding: 20px; border-radius: 8px;">
                <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px;">Resolved Clarification Amount</div>
                <div style="font-size: 20px; font-weight: bold; color: #111827; margin-bottom: 5px;">${formatCurrency(amountSummary.resolvedClarificationAmount || 0)}</div>
                <div style="font-size: 13px; color: #14b8a6;">${amountSummary.resolvedClarificationAmountPercentage != null ? `${amountSummary.resolvedClarificationAmountPercentage.toFixed(1)}% resolved` : '0% resolved'}</div>
              </div>
            </div>
          </div>
        `;
      }

      // Add Status Breakdown if available
      if (statusBreakdown && statusBreakdown.length > 0) {
        htmlContent += `
          <div style="margin-bottom: 30px;">
            <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #111827;">Status Breakdown</h3>
            <div style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px;">
        `;
        
        statusBreakdown.forEach(status => {
          htmlContent += `
            <div style="background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 15px; text-align: center;">
              <div style="display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 10px;">
                <div style="width: 10px; height: 10px; border-radius: 50%; background: ${STATUS_COLORS[status.status]};"></div>
                <div style="font-size: 13px; color: #374151; font-weight: 500;">${STATUS_LABELS[status.status] || status.status}</div>
              </div>
              <div style="font-size: 24px; font-weight: bold; color: #111827; margin-bottom: 5px;">${status.count}</div>
              <div style="font-size: 14px; color: #6b7280; font-weight: 600;">${status.percentage.toFixed(1)}%</div>
            </div>
          `;
        });
        
        htmlContent += `
            </div>
          </div>
        `;
      }

      // Add Charts section if available (show first)
      if (statusBreakdown && statusBreakdown.length > 0) {
        htmlContent += `
          <div style="margin-bottom: 30px;">
            <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #111827;">Charts & Analytics</h3>
            <div id="charts-placeholder" style="min-height: 300px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px;">
              <p style="text-align: center; color: #6b7280; margin: 140px 0;">Loading charts...</p>
            </div>
          </div>
        `;
      }

      htmlContent += `
        </div>
      `;

      printContainer.innerHTML = htmlContent;

      // Wait for content to render
      await new Promise(resolve => setTimeout(resolve, 300));

      // Now capture the charts from the actual page
      const chartsSection = document.querySelector('.charts-grid');
      let chartsCanvas = null;
      
      if (chartsSection && statusBreakdown && statusBreakdown.length > 0) {
        try {
          chartsCanvas = await html2canvas(chartsSection, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
          });
          
          // Insert the chart image into the placeholder
          const chartsPlaceholder = printContainer.querySelector('#charts-placeholder');
          if (chartsPlaceholder && chartsCanvas) {
            const chartsImg = document.createElement('img');
            chartsImg.src = chartsCanvas.toDataURL('image/png');
            chartsImg.style.width = '100%';
            chartsImg.style.height = 'auto';
            chartsImg.style.display = 'block';
            chartsPlaceholder.innerHTML = '';
            chartsPlaceholder.appendChild(chartsImg);
            
            // Wait for image to load
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        } catch (error) {
          console.error('Error capturing charts:', error);
        }
      }

      // Capture first page content as canvas
      const firstPageCanvas = await html2canvas(printContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Remove first page container
      document.body.removeChild(printContainer);

      // Create PDF with first page
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const firstPageImgData = firstPageCanvas.toDataURL('image/png');
      const firstPageImgWidth = firstPageCanvas.width;
      const firstPageImgHeight = firstPageCanvas.height;
      
      // Calculate dimensions for first page
      const firstPageRatio = pdfWidth / firstPageImgWidth;
      const firstPageScaledHeight = firstPageImgHeight * firstPageRatio;
      
      // Add first page
      if (firstPageScaledHeight <= pdfHeight) {
        const imgX = (pdfWidth - firstPageImgWidth * firstPageRatio) / 2;
        pdf.addImage(firstPageImgData, 'PNG', imgX, 0, firstPageImgWidth * firstPageRatio, firstPageScaledHeight);
      } else {
        // First page needs multiple pages
        let heightLeft = firstPageScaledHeight;
        let position = 0;
        
        pdf.addImage(firstPageImgData, 'PNG', 0, position, pdfWidth, firstPageScaledHeight);
        heightLeft -= pdfHeight;
        
        while (heightLeft > 0) {
          position = heightLeft - firstPageScaledHeight;
          pdf.addPage();
          pdf.addImage(firstPageImgData, 'PNG', 0, position, pdfWidth, firstPageScaledHeight);
          heightLeft -= pdfHeight;
        }
      }

      const financialDataForPdf = (single?.checkWiseFinancials || [])
        .filter(item => item.totalChecks > 0)
        .filter(item => financialCheckTypeVisibility[item.checkType] !== false);
      const statusDataForPdf = (single?.checkWiseStatusBreakDown || [])
        .filter(item => item.totalChecks > 0);

      // Create second page container for Financial Summary only
      // (Status Breakdown is always moved to dedicated third page)
      if (financialDataForPdf.length > 0 || statusDataForPdf.length > 0) {
        
        const secondPageContainer = document.createElement('div');
        secondPageContainer.style.position = 'absolute';
        secondPageContainer.style.left = '-9999px';
        secondPageContainer.style.top = '0';
        secondPageContainer.style.width = '1200px';
        secondPageContainer.style.background = 'white';
        secondPageContainer.style.padding = '40px';
        document.body.appendChild(secondPageContainer);

        let secondPageContent = `
          <div style="font-family: Arial, sans-serif; color: #111827;">
        `;

        // Add Financial Summary by Check Type (full section starts on second page)
        if (financialDataForPdf.length > 0) {
            const visibleSeriesLabels = [];
            if (financialSeriesVisibility.postedPercentage) visibleSeriesLabels.push('Posted %');
            if (financialSeriesVisibility.otherPercentage) visibleSeriesLabels.push('Other %');
            if (financialSeriesVisibility.remainingPercentage) visibleSeriesLabels.push('Remaining %');
            secondPageContent += `
              <div style="margin-bottom: 30px; page-break-inside: avoid;">
                <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #111827; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px;">Financial Summary by Check Type</h3>
                ${financialViewMode === 'chart' ? `
                <div style="margin-bottom: 8px; font-size: 13px; color: #6b7280;">Percentage Distribution by Check Type + Total Amount by Check Type</div>
                <div style="margin-bottom: 4px; font-size: 12px; color: #6b7280;"><strong>Visible Distribution Series:</strong> ${visibleSeriesLabels.join(', ') || 'None'}</div>
                <div id="financial-chart-second-page-placeholder" style="min-height: 400px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin-bottom: 15px;">
                  <p style="text-align: center; color: #6b7280; margin: 180px 0;">Loading chart...</p>
                </div>
                ` : ''}
                <div style="margin-bottom: 8px; font-size: 12px; color: #6b7280;"><strong>Visible Check Types:</strong> ${financialDataForPdf.map(item => item.checkType).join(', ')}</div>
                <table style="width: 100%; border-collapse: collapse; font-size: 11px; background: white; margin-top: ${financialViewMode === 'chart' ? '15px' : '0'};">
                  <thead>
                    <tr style="background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%); border-bottom: 2px solid #e5e7eb;">
                      <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Check Type</th>
                      <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Total Checks</th>
                      <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Total Amount</th>
                      ${financialSeriesVisibility.postedPercentage ? `
                      <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Posted Amount</th>
                      <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Posted %</th>
                      ` : ''}
                      ${financialSeriesVisibility.otherPercentage ? `
                      <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Other Amount</th>
                      <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Other %</th>
                      ` : ''}
                      ${financialSeriesVisibility.remainingPercentage ? `
                      <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Remaining Amount</th>
                      <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Remaining %</th>
                      ` : ''}
                    </tr>
                  </thead>
                  <tbody>
            `;
            
            financialDataForPdf.forEach((item, index) => {
              secondPageContent += `
                <tr style="border-bottom: 1px solid #e5e7eb; ${index % 2 === 0 ? 'background: #f9fafb;' : 'background: white;'}">
                  <td style="padding: 8px; border: 1px solid #e5e7eb; font-weight: 600;">
                    <span style="display: inline-block; padding: 4px 10px; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); color: #1e40af; border-radius: 4px; font-size: 10px; font-weight: 600; text-transform: uppercase;">${item.checkType}</span>
                  </td>
                  <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left; font-weight: 600;">${item.totalChecks}</td>
                  <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left; font-weight: 600; font-family: 'Courier New', monospace;">${formatCurrency(item.totalCheckAmount)}</td>
                  ${financialSeriesVisibility.postedPercentage ? `
                  <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left; font-weight: 600; font-family: 'Courier New', monospace;">${formatCurrency(item.totalPostedAmount)}</td>
                  <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left;">
                    <span style="padding: 2px 6px; background: #f3f4f6; border-radius: 3px; font-size: 10px; font-weight: 600;">${item.totalPostedPercentage.toFixed(2)}%</span>
                  </td>
                  ` : ''}
                  ${financialSeriesVisibility.otherPercentage ? `
                  <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left; font-weight: 600; font-family: 'Courier New', monospace;">${formatCurrency(item.totalOtherAmount)}</td>
                  <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left;">
                    <span style="padding: 2px 6px; background: #f3f4f6; border-radius: 3px; font-size: 10px; font-weight: 600;">${item.totalOtherAmountPercentage.toFixed(2)}%</span>
                  </td>
                  ` : ''}
                  ${financialSeriesVisibility.remainingPercentage ? `
                  <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left; font-weight: 600; font-family: 'Courier New', monospace;">${formatCurrency(item.totalRemainingAmount)}</td>
                  <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left;">
                    <span style="padding: 2px 6px; background: #fef3c7; border-radius: 3px; font-size: 10px; font-weight: 600; color: #92400e;">${item.totalRemainingAmountPercentage.toFixed(2)}%</span>
                  </td>
                  ` : ''}
                </tr>
              `;
            });
            
            secondPageContent += `
                  </tbody>
                </table>
              </div>
            `;
        } else {
          secondPageContent += `
            <div style="margin-bottom: 30px; page-break-inside: avoid;">
              <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #111827; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px;">Financial Summary by Check Type</h3>
              <div style="font-size: 13px; color: #6b7280;">No financial data available for selected filters.</div>
            </div>
          `;
        }

        secondPageContent += `
            </div>
          </div>
        `;

        secondPageContainer.innerHTML = secondPageContent;

        // Wait for second page content to render
        await new Promise(resolve => setTimeout(resolve, 300));

        // Capture financial chart into second page if chart view is active
        if (financialViewMode === 'chart' && filteredFinancialChartData.length > 0) {
          const financialSections = document.querySelectorAll('.kpi-table-section.status-breakdown-section');
          let financialChartSection = null;
          for (const section of financialSections) {
            const title = section.querySelector('h2.section-title');
            if (title && title.textContent.includes('Financial Summary')) {
              financialChartSection = section.querySelector('.combined-chart-view');
              break;
            }
          }
          if (financialChartSection) {
            try {
              await new Promise(resolve => setTimeout(resolve, 500));
              const financialChartCanvas = await html2canvas(financialChartSection, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                allowTaint: true
              });
              const financialChartPlaceholder = secondPageContainer.querySelector('#financial-chart-second-page-placeholder');
              if (financialChartPlaceholder && financialChartCanvas) {
                const financialChartImg = document.createElement('img');
                financialChartImg.src = financialChartCanvas.toDataURL('image/png');
                financialChartImg.style.width = '100%';
                financialChartImg.style.height = 'auto';
                financialChartImg.style.display = 'block';
                financialChartPlaceholder.innerHTML = '';
                financialChartPlaceholder.appendChild(financialChartImg);
                await new Promise(resolve => setTimeout(resolve, 300));
              }
            } catch (error) {
              console.error('Error capturing financial chart for second page:', error);
            }
          }
        }

        // Capture second page as canvas
        const secondPageCanvas = await html2canvas(secondPageContainer, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });

        // Remove second page container
        document.body.removeChild(secondPageContainer);

        // Add second page to PDF
        pdf.addPage();
        const secondPageImgData = secondPageCanvas.toDataURL('image/png');
        const secondPageImgWidth = secondPageCanvas.width;
        const secondPageImgHeight = secondPageCanvas.height;
        
        const secondPageRatio = pdfWidth / secondPageImgWidth;
        const secondPageScaledHeight = secondPageImgHeight * secondPageRatio;
        
        if (secondPageScaledHeight <= pdfHeight) {
          const imgX = (pdfWidth - secondPageImgWidth * secondPageRatio) / 2;
          pdf.addImage(secondPageImgData, 'PNG', imgX, 0, secondPageImgWidth * secondPageRatio, secondPageScaledHeight);
        } else {
          // Second page needs multiple pages
          let heightLeft = secondPageScaledHeight;
          let position = 0;
          
          pdf.addImage(secondPageImgData, 'PNG', 0, position, pdfWidth, secondPageScaledHeight);
          heightLeft -= pdfHeight;
          
          while (heightLeft > 0) {
            position = heightLeft - secondPageScaledHeight;
            pdf.addPage();
            pdf.addImage(secondPageImgData, 'PNG', 0, position, pdfWidth, secondPageScaledHeight);
            heightLeft -= pdfHeight;
          }
        }
      }

      // Create dedicated third page container for Status Breakdown by Check Type
      if (statusDataForPdf.length > 0) {
        const thirdPageContainer = document.createElement('div');
        thirdPageContainer.style.position = 'absolute';
        thirdPageContainer.style.left = '-9999px';
        thirdPageContainer.style.top = '0';
        thirdPageContainer.style.width = '1200px';
        thirdPageContainer.style.background = 'white';
        thirdPageContainer.style.padding = '40px';
        document.body.appendChild(thirdPageContainer);

        let thirdPageContent = `
          <div style="font-family: Arial, sans-serif; color: #111827;">
            <div style="margin-bottom: 30px; page-break-inside: avoid;">
              <h3 style="margin: 0 0 15px 0; font-size: 20px; color: #111827; border-bottom: 2px solid #e5e7eb; padding-bottom: 10px;">Status Breakdown by Check Type</h3>
        `;

        if (statusViewMode === 'chart') {
          thirdPageContent += `
            <div id="status-chart-third-page-placeholder" style="min-height: 400px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 20px; margin-bottom: 15px;">
              <p style="text-align: center; color: #6b7280; margin: 180px 0;">Loading chart...</p>
            </div>
          `;
        }

        thirdPageContent += `
              <table style="width: 100%; border-collapse: collapse; font-size: 11px; background: white; margin-top: ${statusViewMode === 'chart' ? '15px' : '0'};">
                <thead>
                  <tr style="background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%); border-bottom: 2px solid #e5e7eb;">
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Check Type</th>
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Total Checks</th>
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Not Started</th>
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">In Progress</th>
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Completed</th>
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Clarifications</th>
                    <th style="padding: 10px 8px; text-align: left; font-weight: 600; color: #374151; border: 1px solid #e5e7eb;">Over Posted</th>
                  </tr>
                </thead>
                <tbody>
        `;

        statusDataForPdf.forEach((item, index) => {
          thirdPageContent += `
            <tr style="border-bottom: 1px solid #e5e7eb; ${index % 2 === 0 ? 'background: #f9fafb;' : 'background: white;'}">
              <td style="padding: 8px; border: 1px solid #e5e7eb; font-weight: 600;">
                <span style="display: inline-block; padding: 4px 10px; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); color: #1e40af; border-radius: 4px; font-size: 10px; font-weight: 600; text-transform: uppercase;">${item.checkType}</span>
              </td>
              <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left; font-weight: 600;">${item.totalChecks}</td>
              <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left;">
                <div style="display: flex; flex-direction: row; align-items: baseline; gap: 4px;">
                  <span style="font-weight: 700; font-size: 13px;">${item.notStartedCount}</span>
                  <span style="font-size: 11px; color: #6b7280;">(${item.notStartedPercentage.toFixed(1)}%)</span>
                </div>
              </td>
              <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left;">
                <div style="display: flex; flex-direction: row; align-items: baseline; gap: 4px;">
                  <span style="font-weight: 700; font-size: 13px;">${item.inProgressCount}</span>
                  <span style="font-size: 11px; color: #6b7280;">(${item.inProgressPercentage.toFixed(1)}%)</span>
                </div>
              </td>
              <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left;">
                <div style="display: flex; flex-direction: row; align-items: baseline; gap: 4px;">
                  <span style="font-weight: 700; font-size: 13px;">${item.completedCount}</span>
                  <span style="font-size: 11px; color: #6b7280;">(${item.completedPercentage.toFixed(1)}%)</span>
                </div>
              </td>
              <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left;">
                <div style="display: flex; flex-direction: row; align-items: baseline; gap: 4px;">
                  <span style="font-weight: 700; font-size: 13px;">${item.underClarificationsCount}</span>
                  <span style="font-size: 11px; color: #6b7280;">(${item.underClarificationsPercentage.toFixed(1)}%)</span>
                </div>
              </td>
              <td style="padding: 8px; border: 1px solid #e5e7eb; text-align: left;">
                <div style="display: flex; flex-direction: row; align-items: baseline; gap: 4px;">
                  <span style="font-weight: 700; font-size: 13px;">${item.overPostedCount}</span>
                  <span style="font-size: 11px; color: #6b7280;">(${item.overPostedPercentage.toFixed(1)}%)</span>
                </div>
              </td>
            </tr>
          `;
        });

        thirdPageContent += `
                </tbody>
              </table>
            </div>
          </div>
        `;

        thirdPageContainer.innerHTML = thirdPageContent;
        await new Promise(resolve => setTimeout(resolve, 300));

        if (statusViewMode === 'chart' && prepareAllCheckTypesChartData().length > 0) {
          const statusSections = document.querySelectorAll('.kpi-table-section.status-breakdown-section');
          let statusChartSection = null;
          for (const section of statusSections) {
            const title = section.querySelector('h2.section-title');
            if (title && title.textContent.includes('Status Breakdown')) {
              statusChartSection = section.querySelector('.combined-chart-view');
              break;
            }
          }
          if (statusChartSection) {
            try {
              await new Promise(resolve => setTimeout(resolve, 500));
              const statusChartCanvas = await html2canvas(statusChartSection, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                allowTaint: true
              });
              const statusChartPlaceholder = thirdPageContainer.querySelector('#status-chart-third-page-placeholder');
              if (statusChartPlaceholder && statusChartCanvas) {
                const statusChartImg = document.createElement('img');
                statusChartImg.src = statusChartCanvas.toDataURL('image/png');
                statusChartImg.style.width = '100%';
                statusChartImg.style.height = 'auto';
                statusChartImg.style.display = 'block';
                statusChartPlaceholder.innerHTML = '';
                statusChartPlaceholder.appendChild(statusChartImg);
                await new Promise(resolve => setTimeout(resolve, 300));
              }
            } catch (error) {
              console.error('Error capturing status chart for third page:', error);
            }
          }
        }

        const thirdPageCanvas = await html2canvas(thirdPageContainer, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });
        document.body.removeChild(thirdPageContainer);

        pdf.addPage();
        const thirdPageImgData = thirdPageCanvas.toDataURL('image/png');
        const thirdPageImgWidth = thirdPageCanvas.width;
        const thirdPageImgHeight = thirdPageCanvas.height;
        const thirdPageRatio = pdfWidth / thirdPageImgWidth;
        const thirdPageScaledHeight = thirdPageImgHeight * thirdPageRatio;

        if (thirdPageScaledHeight <= pdfHeight) {
          const imgX = (pdfWidth - thirdPageImgWidth * thirdPageRatio) / 2;
          pdf.addImage(thirdPageImgData, 'PNG', imgX, 0, thirdPageImgWidth * thirdPageRatio, thirdPageScaledHeight);
        } else {
          let heightLeft = thirdPageScaledHeight;
          let position = 0;

          pdf.addImage(thirdPageImgData, 'PNG', 0, position, pdfWidth, thirdPageScaledHeight);
          heightLeft -= pdfHeight;

          while (heightLeft > 0) {
            position = heightLeft - thirdPageScaledHeight;
            pdf.addPage();
            pdf.addImage(thirdPageImgData, 'PNG', 0, position, pdfWidth, thirdPageScaledHeight);
            heightLeft -= pdfHeight;
          }
        }
      }

      // Save PDF
      const fileName = `Checks_Dashboard_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);


    } catch (error) {
      console.error('Error generating PDF:', error);
      showError('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const EXCLUDE_FIELD_MAP = {
    statuses: 'excludeStatuses',
    practiceCodes: 'excludePracticeCodes',
    locationCodes: 'excludeLocationCodes',
    checkTypes: 'excludeCheckTypes',
    workflowStatuses: 'excludeWorkflowStatuses',
    assigneeIds: 'excludeAssigneeIds',
    reporterIds: 'excludeReporterIds'
  };

  const EXCLUDE_FIELD_TO_INCLUDE_FIELD = {
    excludeStatuses: 'statuses',
    excludeWorkflowStatuses: 'workflowStatuses',
    excludePracticeCodes: 'practiceCodes',
    excludeLocationCodes: 'locationCodes',
    excludeCheckTypes: 'checkTypes',
    excludeAssigneeIds: 'assigneeIds',
    excludeReporterIds: 'reporterIds'
  };

  const handleAddToArray = (field, value) => {
    if (!value || value === '') return;
    const includeField = EXCLUDE_FIELD_TO_INCLUDE_FIELD[field];
    if (includeField) {
      setFilterExcludeUiIntent((intentPrev) => {
        const next = { ...intentPrev };
        delete next[includeField];
        return next;
      });
    }
    setLocalFilters((prev) => {
      const currentArray = prev[field] || [];
      if (currentArray.includes(value)) {
        return prev;
      }
      return {
        ...prev,
        [field]: [...currentArray, value]
      };
    });
  };

  const handleRemoveFromArray = (field, value) => {
    let includeFieldForEmptyExclude = null;
    setLocalFilters((prev) => {
      const currentArray = prev[field] || [];
      const newArray = currentArray.filter((item) => item !== value);
      const includeField = EXCLUDE_FIELD_TO_INCLUDE_FIELD[field];
      if (includeField && newArray.length === 0) {
        includeFieldForEmptyExclude = includeField;
      }
      const next = {
        ...prev,
        [field]: newArray
      };
      if (field === 'practiceCodes' || field === 'excludePracticeCodes') {
        next.locationCodes = [];
        next.excludeLocationCodes = [];
      }
      return next;
    });
    if (includeFieldForEmptyExclude) {
      setFilterExcludeUiIntent((intentPrev) => ({
        ...intentPrev,
        [includeFieldForEmptyExclude]: true
      }));
    }
  };

  const isExcludeMode = (field) => {
    const excludeField = EXCLUDE_FIELD_MAP[field];
    const values = localFilters[excludeField];
    if (Array.isArray(values) && values.length > 0) return true;
    return !!filterExcludeUiIntent[field];
  };

  const getActiveField = (field) => (isExcludeMode(field) ? EXCLUDE_FIELD_MAP[field] : field);
  const getSelectedValues = (field) => localFilters[getActiveField(field)] || [];

  const handleModeToggle = (field, mode) => {
    const excludeField = EXCLUDE_FIELD_MAP[field];
    if (!excludeField) return;
    const includeValues = localFilters[field] || [];
    const excludeValues = localFilters[excludeField] || [];

    if (mode === 'exclude') {
      const newExclude = includeValues.length ? includeValues : excludeValues;
      setLocalFilters((prev) => ({
        ...prev,
        [field]: [],
        [excludeField]: newExclude
      }));
      setFilterExcludeUiIntent((prev) => {
        const next = { ...prev };
        if (newExclude.length === 0) next[field] = true;
        else delete next[field];
        return next;
      });
      return;
    }

    setLocalFilters((prev) => ({
      ...prev,
      [field]: excludeValues.length ? excludeValues : includeValues,
      [excludeField]: []
    }));
    setFilterExcludeUiIntent((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleAddFilterValue = (field, value) => {
    if (!value) return;
    handleAddToArray(getActiveField(field), value);
  };

  const handleRemoveFilterValue = (field, value) => {
    handleRemoveFromArray(getActiveField(field), value);
  };

  const renderModeLabel = (field, label) => {
    const excludeActive = isExcludeMode(field);
    return (
      <div className="filter-label-row">
        <label className={excludeActive ? 'exclude-mode-label' : ''}>{label}</label>
        <div className="include-exclude-controls">
          <button
            type="button"
            className={`filter-mode-switch ${excludeActive ? 'filter-mode-switch--exclude' : 'filter-mode-switch--include'}`}
            role="switch"
            aria-checked={excludeActive}
            aria-label={excludeActive ? 'Exclude mode: selected values are excluded' : 'Include mode: selected values are included'}
            onClick={() => handleModeToggle(field, excludeActive ? 'include' : 'exclude')}
          >
            <span className="filter-mode-switch-thumb" aria-hidden />
          </button>
          <span className={`filter-mode-switch-caption ${excludeActive ? 'filter-mode-switch-caption--exclude' : ''}`}>
            {excludeActive ? 'Exclude' : 'Include'}
          </span>
          <Tooltip text="Include returns matching records. Exclude removes selected values for this column." position="top">
            <span className="exclude-help-dot" aria-hidden>!</span>
          </Tooltip>
        </div>
      </div>
    );
  };

  const handlePracticeRemove = (practiceCode) => {
    handleRemoveFromArray('practiceCodes', practiceCode);
  };

  const applyFilters = () => {
    // Validate date ranges: if a "From" date is selected, the corresponding "To" date must also be selected
    const dateRangePairs = [
      { from: 'depositDateFrom', to: 'depositDateTo', label: 'Deposit Date' },
      { from: 'receivedDateFrom', to: 'receivedDateTo', label: 'Received Date' },
      { from: 'completedDateFrom', to: 'completedDateTo', label: 'Completed Date' }
    ];

    // Check each date range pair
    for (const pair of dateRangePairs) {
      const fromValue = localFilters[pair.from];
      const toValue = localFilters[pair.to];
      
      // If "From" date is set but "To" date is not set, show error
      if (fromValue && fromValue.trim() !== '' && (!toValue || toValue.trim() === '')) {
        showError(`You need to enter end date for ${pair.label} range.`);
        return false; // Don't apply filters if validation fails
      }
      
      // If both dates are set, validate 3-month maximum range
      if (fromValue && fromValue.trim() !== '' && toValue && toValue.trim() !== '') {
        const startDate = new Date(fromValue);
        const endDate = new Date(toValue);
        
        // Calculate difference in months
        const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                           (endDate.getMonth() - startDate.getMonth());
        
        if (monthsDiff > 3) {
          showError(`${pair.label} range can only be maximum 3 months.`);
          return false; // Don't apply filters if validation fails
        }
      }
    }

    const newFilters = {};
    
    if (localFilters.depositDateFrom) {
      newFilters.depositDateFrom = localFilters.depositDateFrom;
    }
    if (localFilters.depositDateTo) {
      newFilters.depositDateTo = localFilters.depositDateTo;
    }
    if (localFilters.receivedDateFrom) {
      newFilters.receivedDateFrom = localFilters.receivedDateFrom;
    }
    if (localFilters.receivedDateTo) {
      newFilters.receivedDateTo = localFilters.receivedDateTo;
    }
    if (localFilters.completedDateFrom) {
      newFilters.completedDateFrom = localFilters.completedDateFrom;
    }
    if (localFilters.completedDateTo) {
      newFilters.completedDateTo = localFilters.completedDateTo;
    }
    if (localFilters.practiceCodes && localFilters.practiceCodes.length > 0) {
      newFilters.practiceCodes = [...localFilters.practiceCodes];
    }
    if (localFilters.excludePracticeCodes && localFilters.excludePracticeCodes.length > 0) {
      newFilters.excludePracticeCodes = [...localFilters.excludePracticeCodes];
    }
    if (localFilters.locationCodes && localFilters.locationCodes.length > 0) {
      newFilters.locationCodes = [...localFilters.locationCodes];
    }
    if (localFilters.excludeLocationCodes && localFilters.excludeLocationCodes.length > 0) {
      newFilters.excludeLocationCodes = [...localFilters.excludeLocationCodes];
    }
    if (localFilters.statuses && localFilters.statuses.length > 0) {
      newFilters.statuses = [...localFilters.statuses];
    }
    if (localFilters.excludeStatuses && localFilters.excludeStatuses.length > 0) {
      newFilters.excludeStatuses = [...localFilters.excludeStatuses];
    }
    if (localFilters.assigneeIds && localFilters.assigneeIds.length > 0) {
      newFilters.assigneeIds = [...localFilters.assigneeIds];
    }
    if (localFilters.excludeAssigneeIds && localFilters.excludeAssigneeIds.length > 0) {
      newFilters.excludeAssigneeIds = [...localFilters.excludeAssigneeIds];
    }
    if (localFilters.reporterIds && localFilters.reporterIds.length > 0) {
      newFilters.reporterIds = [...localFilters.reporterIds];
    }
    if (localFilters.excludeReporterIds && localFilters.excludeReporterIds.length > 0) {
      newFilters.excludeReporterIds = [...localFilters.excludeReporterIds];
    }
    if (localFilters.checkTypes && localFilters.checkTypes.length > 0) {
      newFilters.checkTypes = [...localFilters.checkTypes];
    }
    if (localFilters.excludeCheckTypes && localFilters.excludeCheckTypes.length > 0) {
      newFilters.excludeCheckTypes = [...localFilters.excludeCheckTypes];
    }
    if (localFilters.workflowStatuses && localFilters.workflowStatuses.length > 0) {
      newFilters.workflowStatuses = [...localFilters.workflowStatuses];
    }
    if (localFilters.excludeWorkflowStatuses && localFilters.excludeWorkflowStatuses.length > 0) {
      newFilters.excludeWorkflowStatuses = [...localFilters.excludeWorkflowStatuses];
    }
    if (localFilters.checkNumber && localFilters.checkNumber.trim() !== '') {
      newFilters.checkNumber = localFilters.checkNumber.trim().toUpperCase();
    }
    if (localFilters.payer && localFilters.payer.trim() !== '') {
      newFilters.payer = localFilters.payer.trim();
    }
    if (localFilters.unknown !== undefined && localFilters.unknown !== null) {
      newFilters.unknown = localFilters.unknown;
    }
    
    setFilters(newFilters);

    return true;
  };

  const clearFilters = () => {
    setLocalFilters({
      depositDateFrom: '',
      depositDateTo: '',
      receivedDateFrom: '',
      receivedDateTo: '',
      completedDateFrom: '',
      completedDateTo: '',
      practiceCodes: [],
      excludePracticeCodes: [],
      locationCodes: [],
      excludeLocationCodes: [],
      statuses: [],
      excludeStatuses: [],
      assigneeIds: [],
      excludeAssigneeIds: [],
      reporterIds: [],
      excludeReporterIds: [],
      checkTypes: [],
      excludeCheckTypes: [],
      workflowStatuses: [],
      excludeWorkflowStatuses: [],
      checkNumber: '',
      payer: '',
      unknown: undefined
    });
    setFilterExcludeUiIntent({});
    setFilters({});
  };

  const hasActiveFilters = () => {
    return (
      filters.depositDateFrom ||
      filters.depositDateTo ||
      filters.receivedDateFrom ||
      filters.receivedDateTo ||
      filters.completedDateFrom ||
      filters.completedDateTo ||
      (filters.practiceCodes && filters.practiceCodes.length > 0) ||
      (filters.excludePracticeCodes && filters.excludePracticeCodes.length > 0) ||
      (filters.locationCodes && filters.locationCodes.length > 0) ||
      (filters.excludeLocationCodes && filters.excludeLocationCodes.length > 0) ||
      (filters.statuses && filters.statuses.length > 0) ||
      (filters.excludeStatuses && filters.excludeStatuses.length > 0) ||
      (filters.workflowStatuses && filters.workflowStatuses.length > 0) ||
      (filters.excludeWorkflowStatuses && filters.excludeWorkflowStatuses.length > 0) ||
      (filters.assigneeIds && filters.assigneeIds.length > 0) ||
      (filters.excludeAssigneeIds && filters.excludeAssigneeIds.length > 0) ||
      (filters.reporterIds && filters.reporterIds.length > 0) ||
      (filters.excludeReporterIds && filters.excludeReporterIds.length > 0) ||
      (filters.checkTypes && filters.checkTypes.length > 0) ||
      (filters.excludeCheckTypes && filters.excludeCheckTypes.length > 0) ||
      (filters.checkNumber && filters.checkNumber.trim() !== '') ||
      (filters.payer && filters.payer.trim() !== '') ||
      (filters.unknown !== undefined && filters.unknown !== null)
    );
  };

  const getAppliedFilters = () => {
    const applied = {};
    if (filters.depositDateFrom) applied.depositDateFrom = filters.depositDateFrom;
    if (filters.depositDateTo) applied.depositDateTo = filters.depositDateTo;
    if (filters.receivedDateFrom) applied.receivedDateFrom = filters.receivedDateFrom;
    if (filters.receivedDateTo) applied.receivedDateTo = filters.receivedDateTo;
    if (filters.completedDateFrom) applied.completedDateFrom = filters.completedDateFrom;
    if (filters.completedDateTo) applied.completedDateTo = filters.completedDateTo;
    if (filters.practiceCodes) applied.practiceCodes = filters.practiceCodes;
    if (filters.excludePracticeCodes) applied.excludePracticeCodes = filters.excludePracticeCodes;
    if (filters.locationCodes) applied.locationCodes = filters.locationCodes;
    if (filters.excludeLocationCodes) applied.excludeLocationCodes = filters.excludeLocationCodes;
    if (filters.statuses) applied.statuses = filters.statuses;
    if (filters.excludeStatuses) applied.excludeStatuses = filters.excludeStatuses;
    if (filters.assigneeIds) applied.assigneeIds = filters.assigneeIds;
    if (filters.excludeAssigneeIds) applied.excludeAssigneeIds = filters.excludeAssigneeIds;
    if (filters.reporterIds) applied.reporterIds = filters.reporterIds;
    if (filters.excludeReporterIds) applied.excludeReporterIds = filters.excludeReporterIds;
    if (filters.checkTypes) applied.checkTypes = filters.checkTypes;
    if (filters.excludeCheckTypes) applied.excludeCheckTypes = filters.excludeCheckTypes;
    if (filters.workflowStatuses) applied.workflowStatuses = filters.workflowStatuses;
    if (filters.excludeWorkflowStatuses) applied.excludeWorkflowStatuses = filters.excludeWorkflowStatuses;
    if (filters.checkNumber && filters.checkNumber.trim() !== '') applied.checkNumber = filters.checkNumber;
    if (filters.payer && filters.payer.trim() !== '') applied.payer = filters.payer;
    if (filters.unknown !== undefined && filters.unknown !== null) applied.unknown = filters.unknown;
    return applied;
  };

  const STATUS_COLORS = {
    'NOT_STARTED': '#6b7280',
    'IN_PROGRESS': '#3b82f6',
    'COMPLETED': '#10b981',
    'OVER_POSTED': '#ef4444',
    'UNDER_CLARIFICATIONS': '#f59e0b'
  };

  const STATUS_LABELS = {
    'NOT_STARTED': 'Not Started',
    'IN_PROGRESS': 'In Progress',
    'COMPLETED': 'Completed',
    'OVER_POSTED': 'Over Posted',
    'UNDER_CLARIFICATIONS': 'Clarifications'
  };

  const prepareStatusChartData = () => {
    if (!kpiData?.single?.statusBreakdown) return [];
    return kpiData.single.statusBreakdown.map(item => ({
      name: STATUS_LABELS[item.status] || item.status,
      value: item.count,
      percentage: item.percentage,
      status: item.status
    }));
  };

  const prepareUnknownKnownChartData = () => {
    const unknown = unknownCount || 0;
    const known = knownCount || 0;
    const total = unknown + known;
    
    return [
      {
        name: 'Known',
        count: known,
        percentage: total > 0 ? (known / total) * 100 : 0,
        color: '#10b981'
      },
      {
        name: 'Unknown',
        count: unknown,
        percentage: total > 0 ? (unknown / total) * 100 : 0,
        color: '#f59e0b'
      }
    ];
  };

  const prepareAllCheckTypesChartData = () => {
    if (!single?.checkWiseStatusBreakDown) return [];
    
    const checkTypes = single.checkWiseStatusBreakDown.filter(item => item.totalChecks > 0);
    
    // Transform data for grouped bar chart
    const chartData = checkTypes.map(item => ({
      checkType: item.checkType,
      'Not Started': item.notStartedCount || 0,
      'In Progress': item.inProgressCount || 0,
      'Completed': item.completedCount || 0,
      'Clarifications': item.underClarificationsCount || 0,
      'Over Posted': item.overPostedCount || 0,
      total: item.totalChecks
    }));
    
    return chartData;
  };

  const prepareFinancialChartData = () => {
    if (!single?.checkWiseFinancials) return [];
    return single.checkWiseFinancials
      .filter(item => item.totalChecks > 0)
      .map(item => ({
        checkType: item.checkType,
        postedPercentage: item.totalPostedPercentage != null ? Number(item.totalPostedPercentage) : 0,
        otherPercentage: item.totalOtherAmountPercentage != null ? Number(item.totalOtherAmountPercentage) : 0,
        remainingPercentage: item.totalRemainingAmountPercentage != null ? Number(item.totalRemainingAmountPercentage) : 0,
        totalAmount: item.totalCheckAmount != null ? Number(item.totalCheckAmount) : 0,
        postedAmount: item.totalPostedAmount != null ? Number(item.totalPostedAmount) : 0,
        otherAmount: item.totalOtherAmount != null ? Number(item.totalOtherAmount) : 0,
        remainingAmount: item.totalRemainingAmount != null ? Number(item.totalRemainingAmount) : 0,
        totalChecks: item.totalChecks
      }))
      .sort((a, b) => b.totalChecks - a.totalChecks);
  };

  // Extract data safely - may be null if API failed
  const { single } = kpiData || {};
  const { totalCount, statusBreakdown, amountSummary, unknownCount, knownCount } = single || {};
  const financialChartData = prepareFinancialChartData();
  const filteredFinancialChartData = financialChartData.filter(
    item => financialCheckTypeVisibility[item.checkType] !== false
  );
  const hoveredFinancialData = hoveredFinancialCheckType
    ? filteredFinancialChartData.find(item => item.checkType === hoveredFinancialCheckType) || null
    : null;
  const renderFinancialCheckTypeTick = ({ x, y, payload }) => {
    const value = payload?.value;
    const isHovered = hoveredFinancialCheckType === value;
    const isDimmed = hoveredFinancialCheckType && !isHovered;

    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor="end"
        fill={isHovered ? '#111827' : '#6b7280'}
        style={{
          fontSize: 12,
          fontWeight: isHovered ? 700 : 500,
          opacity: isDimmed ? 0.45 : 1
        }}
      >
        {value}
      </text>
    );
  };

  useEffect(() => {
    if (!financialChartData.length) return;
    setFinancialCheckTypeVisibility(prev => {
      const next = {};
      financialChartData.forEach(item => {
        next[item.checkType] = prev[item.checkType] ?? true;
      });

      const sameKeys = Object.keys(prev).length === Object.keys(next).length;
      const sameValues = sameKeys && Object.keys(next).every(key => prev[key] === next[key]);
      return sameValues ? prev : next;
    });
  }, [financialChartData]);

  // Check dashboard permission - using CHECK_DASHBOARD_VIEW as the main dashboard permission
  if (!hasDashboardPermission) {
    return (
      <div className="dashboard">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 100px)',
          textAlign: 'center',
          padding: '40px 20px',
          background: '#f9fafb'
        }}>
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '48px 32px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            maxWidth: '500px',
            width: '100%'
          }}>
            <div style={{
              fontSize: '64px',
              marginBottom: '24px'
            }}>
              🔒
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              margin: '0 0 12px 0'
            }}>
              Access Denied
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              You don't have permission to view the Dashboard.
            </p>
            <p style={{
              fontSize: '14px',
              color: '#9ca3af',
              margin: '0',
              lineHeight: '1.5'
            }}>
              Please contact your administrator to request access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Always render dashboard, even when API fails
  
  // Check if we have amount data available (hide amount cards if API failed)
  const hasAmountData = kpiData && amountSummary;

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1 className="page-title">Checks Dashboard</h1>
          <p className="page-subtitle">
            {getSubtitle()}
          </p>
        </div>
        <div className="header-actions">
          {!hasActiveFilters() && (
            <div className="date-range-container">
              <button 
                className="date-range-btn"
                onClick={() => setShowMonthPicker(!showMonthPicker)}
              >
                {getDateRangeDisplay()}
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ marginLeft: '8px' }}>
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showMonthPicker && (
                <MonthYearPicker
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onSelect={handleMonthYearSelect}
                  onClose={() => setShowMonthPicker(false)}
                />
              )}
            </div>
          )}
          {hasActiveFilters() && (
            <div className="date-range-container">
              <div className="date-range-display">
                {getDateRangeDisplay()}
              </div>
            </div>
          )}
          <button 
            onClick={exportToPDF}
            disabled={isExporting}
            className="custom-filter-button export-pdf-button"
            title="Export dashboard to PDF"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
            </svg>
            {isExporting ? 'Generating...' : 'Export PDF'}
          </button>
          <button 
            onClick={() => setShowFilterSidebar(true)} 
            className={`custom-filter-button ${hasActiveFilters() ? 'has-filters' : ''}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
            Custom Filter
            {hasActiveFilters() && (
              <span className="filter-badge">
                {Object.keys(getAppliedFilters()).length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Active Filters */}
      {hasActiveFilters() && (() => {
        const appliedFilters = getAppliedFilters();
        return (
          <div className="active-filters-section">
            <div className="active-filters-label">Active Filters:</div>
            <div className="active-filters-chips">
              {(appliedFilters.depositDateFrom || appliedFilters.depositDateTo) && (
                <div className="filter-chip">
                  <span>Deposit Date: {formatDateRange(
                    appliedFilters.depositDateFrom || '',
                    appliedFilters.depositDateTo || ''
                  )}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.depositDateFrom;
                      delete newFilters.depositDateTo;
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, depositDateFrom: '', depositDateTo: '' }));
                    }}
                    title="Remove deposit date filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
              {(appliedFilters.receivedDateFrom || appliedFilters.receivedDateTo) && (
                <div className="filter-chip">
                  <span>Received Date: {formatDateRange(
                    appliedFilters.receivedDateFrom || '',
                    appliedFilters.receivedDateTo || ''
                  )}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.receivedDateFrom;
                      delete newFilters.receivedDateTo;
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, receivedDateFrom: '', receivedDateTo: '' }));
                    }}
                    title="Remove received date filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
              {(appliedFilters.completedDateFrom || appliedFilters.completedDateTo) && (
                <div className="filter-chip">
                  <span>Completed Date: {formatDateRange(
                    appliedFilters.completedDateFrom || '',
                    appliedFilters.completedDateTo || ''
                  )}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.completedDateFrom;
                      delete newFilters.completedDateTo;
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, completedDateFrom: '', completedDateTo: '' }));
                    }}
                    title="Remove completed date filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
              {appliedFilters.practiceCodes && appliedFilters.practiceCodes.length > 0 && appliedFilters.practiceCodes.map((practiceCode) => (
                <div key={practiceCode} className="filter-chip">
                  <span>Practice: {practices.find(p => p.code === practiceCode)?.name || practiceCode}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      if (newFilters.practiceCodes) {
                        newFilters.practiceCodes = newFilters.practiceCodes.filter(code => code !== practiceCode);
                        if (newFilters.practiceCodes.length === 0) {
                          delete newFilters.practiceCodes;
                        }
                      }
                      setFilters(newFilters);
                      handlePracticeRemove(practiceCode);
                    }}
                    title="Remove practice filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              ))}
              {appliedFilters.excludePracticeCodes && appliedFilters.excludePracticeCodes.length > 0 && appliedFilters.excludePracticeCodes.map((practiceCode) => (
                <div key={`exclude-${practiceCode}`} className="filter-chip filter-chip--exclude">
                  <span>Practice: Not {practices.find(p => p.code === practiceCode)?.name || practiceCode}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...appliedFilters };
                      if (newFilters.excludePracticeCodes) {
                        newFilters.excludePracticeCodes = newFilters.excludePracticeCodes.filter(code => code !== practiceCode);
                        if (newFilters.excludePracticeCodes.length === 0) delete newFilters.excludePracticeCodes;
                      }
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, excludePracticeCodes: prev.excludePracticeCodes.filter(code => code !== practiceCode) }));
                    }}
                    title="Remove excluded practice filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              ))}
              {appliedFilters.locationCodes && appliedFilters.locationCodes.length > 0 && appliedFilters.locationCodes.map((locationCode) => (
                <div key={locationCode} className="filter-chip">
                  <span>Location: {locations.find(l => l.code === locationCode)?.name || locationCode}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      if (newFilters.locationCodes) {
                        newFilters.locationCodes = newFilters.locationCodes.filter(code => code !== locationCode);
                        if (newFilters.locationCodes.length === 0) {
                          delete newFilters.locationCodes;
                        }
                      }
                      setFilters(newFilters);
                      setLocalFilters(prev => ({
                        ...prev,
                        locationCodes: prev.locationCodes.filter(code => code !== locationCode)
                      }));
                    }}
                    title="Remove location filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              ))}
              {appliedFilters.excludeLocationCodes && appliedFilters.excludeLocationCodes.length > 0 && appliedFilters.excludeLocationCodes.map((locationCode) => (
                <div key={`exclude-${locationCode}`} className="filter-chip filter-chip--exclude">
                  <span>Location: Not {locations.find(l => l.code === locationCode)?.name || locationCode}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...appliedFilters };
                      if (newFilters.excludeLocationCodes) {
                        newFilters.excludeLocationCodes = newFilters.excludeLocationCodes.filter(code => code !== locationCode);
                        if (newFilters.excludeLocationCodes.length === 0) delete newFilters.excludeLocationCodes;
                      }
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, excludeLocationCodes: prev.excludeLocationCodes.filter(code => code !== locationCode) }));
                    }}
                    title="Remove excluded location filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              ))}
              {appliedFilters.statuses && appliedFilters.statuses.length > 0 && appliedFilters.statuses.map((status) => {
                const statusLabels = {
                  'NOT_STARTED': 'Not Started',
                  'IN_PROGRESS': 'In Progress',
                  'UNDER_CLARIFICATIONS': 'Under Clarifications',
                  'COMPLETED': 'Completed',
                  'OVER_POSTED': 'Over Posted'
                };
                return (
                  <div key={status} className="filter-chip">
                    <span>Status: {statusLabels[status] || status}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...filters };
                        if (newFilters.statuses) {
                          newFilters.statuses = newFilters.statuses.filter(s => s !== status);
                          if (newFilters.statuses.length === 0) {
                            delete newFilters.statuses;
                          }
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({
                          ...prev,
                          statuses: prev.statuses.filter(s => s !== status)
                        }));
                      }}
                      title="Remove status filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.excludeStatuses && appliedFilters.excludeStatuses.length > 0 && appliedFilters.excludeStatuses.map((status) => {
                const statusLabels = {
                  'NOT_STARTED': 'Not Started',
                  'IN_PROGRESS': 'In Progress',
                  'UNDER_CLARIFICATIONS': 'Under Clarifications',
                  'COMPLETED': 'Completed',
                  'OVER_POSTED': 'Over Posted'
                };
                return (
                  <div key={`exclude-${status}`} className="filter-chip filter-chip--exclude">
                    <span>Status: Not {statusLabels[status] || status}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...appliedFilters };
                        if (newFilters.excludeStatuses) {
                          newFilters.excludeStatuses = newFilters.excludeStatuses.filter(s => s !== status);
                          if (newFilters.excludeStatuses.length === 0) delete newFilters.excludeStatuses;
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({ ...prev, excludeStatuses: prev.excludeStatuses.filter(s => s !== status) }));
                      }}
                      title="Remove excluded status filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.assigneeIds && appliedFilters.assigneeIds.length > 0 && appliedFilters.assigneeIds.map((assigneeId) => {
                const user = users.find(u => (u.userId || u.id) === assigneeId);
                const label = user && user.firstName && user.lastName 
                  ? `${user.firstName} ${user.lastName}` 
                  : user?.email || assigneeId;
                return (
                  <div key={assigneeId} className="filter-chip">
                    <span>Assignee: {label}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...filters };
                        if (newFilters.assigneeIds) {
                          newFilters.assigneeIds = newFilters.assigneeIds.filter(id => id !== assigneeId);
                          if (newFilters.assigneeIds.length === 0) {
                            delete newFilters.assigneeIds;
                          }
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({
                          ...prev,
                          assigneeIds: prev.assigneeIds.filter(id => id !== assigneeId)
                        }));
                      }}
                      title="Remove assignee filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.excludeAssigneeIds && appliedFilters.excludeAssigneeIds.length > 0 && appliedFilters.excludeAssigneeIds.map((assigneeId) => {
                const user = users.find(u => (u.userId || u.id) === assigneeId);
                const label = user && user.firstName && user.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.email || assigneeId;
                return (
                  <div key={`exclude-assignee-${assigneeId}`} className="filter-chip filter-chip--exclude">
                    <span>Assignee: Not {label}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...filters };
                        if (newFilters.excludeAssigneeIds) {
                          newFilters.excludeAssigneeIds = newFilters.excludeAssigneeIds.filter(id => id !== assigneeId);
                          if (newFilters.excludeAssigneeIds.length === 0) {
                            delete newFilters.excludeAssigneeIds;
                          }
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({
                          ...prev,
                          excludeAssigneeIds: prev.excludeAssigneeIds.filter(id => id !== assigneeId)
                        }));
                      }}
                      title="Remove excluded assignee filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.reporterIds && appliedFilters.reporterIds.length > 0 && appliedFilters.reporterIds.map((reporterId) => {
                const user = users.find(u => (u.userId || u.id) === reporterId);
                const label = user && user.firstName && user.lastName 
                  ? `${user.firstName} ${user.lastName}` 
                  : user?.email || reporterId;
                return (
                  <div key={reporterId} className="filter-chip">
                    <span>Reporter: {label}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...filters };
                        if (newFilters.reporterIds) {
                          newFilters.reporterIds = newFilters.reporterIds.filter(id => id !== reporterId);
                          if (newFilters.reporterIds.length === 0) {
                            delete newFilters.reporterIds;
                          }
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({
                          ...prev,
                          reporterIds: prev.reporterIds.filter(id => id !== reporterId)
                        }));
                      }}
                      title="Remove reporter filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.excludeReporterIds && appliedFilters.excludeReporterIds.length > 0 && appliedFilters.excludeReporterIds.map((reporterId) => {
                const user = users.find(u => (u.userId || u.id) === reporterId);
                const label = user && user.firstName && user.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.email || reporterId;
                return (
                  <div key={`exclude-reporter-${reporterId}`} className="filter-chip filter-chip--exclude">
                    <span>Reporter: Not {label}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...filters };
                        if (newFilters.excludeReporterIds) {
                          newFilters.excludeReporterIds = newFilters.excludeReporterIds.filter(id => id !== reporterId);
                          if (newFilters.excludeReporterIds.length === 0) {
                            delete newFilters.excludeReporterIds;
                          }
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({
                          ...prev,
                          excludeReporterIds: prev.excludeReporterIds.filter(id => id !== reporterId)
                        }));
                      }}
                      title="Remove excluded reporter filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.checkTypes && appliedFilters.checkTypes.length > 0 && appliedFilters.checkTypes.map((checkType) => {
                const checkTypeItem = checkTypes.find(ct => ct.code === checkType);
                const label = checkTypeItem?.name || checkType;
                return (
                  <div key={checkType} className="filter-chip">
                    <span>Check Type: {label}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...filters };
                        if (newFilters.checkTypes) {
                          newFilters.checkTypes = newFilters.checkTypes.filter(ct => ct !== checkType);
                          if (newFilters.checkTypes.length === 0) {
                            delete newFilters.checkTypes;
                          }
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({
                          ...prev,
                          checkTypes: prev.checkTypes.filter(ct => ct !== checkType)
                        }));
                      }}
                      title="Remove check type filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.excludeCheckTypes && appliedFilters.excludeCheckTypes.length > 0 && appliedFilters.excludeCheckTypes.map((checkType) => {
                const checkTypeItem = checkTypes.find(ct => ct.code === checkType);
                const label = checkTypeItem?.name || checkType;
                return (
                  <div key={`exclude-${checkType}`} className="filter-chip filter-chip--exclude">
                    <span>Check Type: Not {label}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...appliedFilters };
                        if (newFilters.excludeCheckTypes) {
                          newFilters.excludeCheckTypes = newFilters.excludeCheckTypes.filter(ct => ct !== checkType);
                          if (newFilters.excludeCheckTypes.length === 0) delete newFilters.excludeCheckTypes;
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({ ...prev, excludeCheckTypes: prev.excludeCheckTypes.filter(ct => ct !== checkType) }));
                      }}
                      title="Remove excluded check type filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.workflowStatuses && appliedFilters.workflowStatuses.length > 0 && appliedFilters.workflowStatuses.map((workflowStatus) => {
                const workflowStatusItem = workflowStatuses.find(ws => ws.code === workflowStatus);
                const label = workflowStatusItem?.name || workflowStatus;
                return (
                  <div key={workflowStatus} className="filter-chip">
                    <span>Workflow Status: {label}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...filters };
                        if (newFilters.workflowStatuses) {
                          newFilters.workflowStatuses = newFilters.workflowStatuses.filter(ws => ws !== workflowStatus);
                          if (newFilters.workflowStatuses.length === 0) {
                            delete newFilters.workflowStatuses;
                          }
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({
                          ...prev,
                          workflowStatuses: prev.workflowStatuses.filter(ws => ws !== workflowStatus)
                        }));
                      }}
                      title="Remove workflow status filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.excludeWorkflowStatuses && appliedFilters.excludeWorkflowStatuses.length > 0 && appliedFilters.excludeWorkflowStatuses.map((workflowStatus) => {
                const workflowStatusItem = workflowStatuses.find(ws => ws.code === workflowStatus);
                const label = workflowStatusItem?.name || workflowStatus;
                return (
                  <div key={`exclude-${workflowStatus}`} className="filter-chip filter-chip--exclude">
                    <span>Workflow Status: Not {label}</span>
                    <button
                      className="chip-close-btn"
                      onClick={() => {
                        const newFilters = { ...appliedFilters };
                        if (newFilters.excludeWorkflowStatuses) {
                          newFilters.excludeWorkflowStatuses = newFilters.excludeWorkflowStatuses.filter(ws => ws !== workflowStatus);
                          if (newFilters.excludeWorkflowStatuses.length === 0) delete newFilters.excludeWorkflowStatuses;
                        }
                        setFilters(newFilters);
                        setLocalFilters(prev => ({ ...prev, excludeWorkflowStatuses: prev.excludeWorkflowStatuses.filter(ws => ws !== workflowStatus) }));
                      }}
                      title="Remove excluded workflow status filter"
                    >
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                  </div>
                );
              })}
              {appliedFilters.checkNumber && (
                <div className="filter-chip">
                  <span>Check: {appliedFilters.checkNumber}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.checkNumber;
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, checkNumber: '' }));
                    }}
                    title="Remove check number filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
              {appliedFilters.payer && (
                <div className="filter-chip">
                  <span>Payer: {appliedFilters.payer}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.payer;
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, payer: '' }));
                    }}
                    title="Remove payer filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
              {appliedFilters.unknown !== undefined && appliedFilters.unknown !== null && (
                <div className="filter-chip">
                  <span>Unknown: {appliedFilters.unknown === true ? 'True' : 'False'}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.unknown;
                      setFilters(newFilters);
                      setLocalFilters(prev => ({ ...prev, unknown: undefined }));
                    }}
                    title="Remove unknown filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
              <button
                className="clear-all-filters-btn"
                onClick={clearFilters}
                title="Clear all filters"
              >
                Clear All
              </button>
            </div>
          </div>
        );
      })()}

      {/* KPI Cards - Only show if we have amount data */}
      {hasAmountData && (
        <div className="stats-grid">
          <div className="stat-card primary">
            <div className="stat-icon-wrapper">
              <div className="stat-icon dollar">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" fill="currentColor"/>
                </svg>
              </div>
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Amount</div>
              <div className="stat-value">{formatCurrency(amountSummary?.totalAmount)}</div>
              <div className="stat-meta">{totalCount || 0} checks</div>
            </div>
          </div>

          <div className="stat-card success">
            <div className="stat-icon-wrapper">
              <div className="stat-icon trend">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 17L9 11L13 15L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 7H15V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className="stat-content">
              <div className="stat-label">Posted Amount</div>
              <div className="stat-value">{formatCurrency(amountSummary?.postedAmount)}</div>
              <div className="stat-meta success">
                {amountSummary?.totalAmount 
                  ? `${((amountSummary.postedAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                  : '0% of total'}
              </div>
            </div>
          </div>

          <div className="stat-card info">
            <div className="stat-icon-wrapper">
              <div className="stat-icon info">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>
            <div className="stat-content">
              <div className="stat-label">Other Amount</div>
              <div className="stat-value">{formatCurrency(amountSummary?.otherAmount)}</div>
              <div className="stat-meta">
                {amountSummary?.totalAmount 
                  ? `${((amountSummary.otherAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                  : '0% of total'}
              </div>
            </div>
          </div>

          <div className="stat-card warning">
            <div className="stat-icon-wrapper">
              <div className="stat-icon warning">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
            </div>
            <div className="stat-content">
              <div className="stat-label">Remaining Amount</div>
              <div className="stat-value">{formatCurrency(amountSummary?.remainingAmount)}</div>
              <div className="stat-meta warning">
                {amountSummary?.totalAmount 
                  ? `${((amountSummary.remainingAmount / amountSummary.totalAmount) * 100).toFixed(1)}% remaining`
                  : '0% remaining'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clarification Amount Cards - New Row */}
      {hasAmountData && (
        <div className="stats-grid">
          <div className="stat-card purple">
            <div className="stat-icon-wrapper">
              <div className="stat-icon purple">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Clarification Amount</div>
              <div className="stat-value">{formatCurrency(amountSummary?.totalClarificationAmount)}</div>
              <div className="stat-meta">
                {amountSummary?.totalAmount 
                  ? `${((amountSummary.totalClarificationAmount / amountSummary.totalAmount) * 100).toFixed(1)}% of total`
                  : '0% of total'}
              </div>
            </div>
          </div>

          <div className="stat-card orange">
            <div className="stat-icon-wrapper">
              <div className="stat-icon orange">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className="stat-content">
              <div className="stat-label">Open Clarification Amount</div>
              <div className="stat-value">{formatCurrency(amountSummary?.openClarificationAmount)}</div>
              <div className="stat-meta orange">
                {amountSummary?.openClarificationAmountPercentage != null
                  ? `${amountSummary.openClarificationAmountPercentage.toFixed(1)}% open`
                  : '0% open'}
              </div>
            </div>
          </div>

          <div className="stat-card teal">
            <div className="stat-icon-wrapper">
              <div className="stat-icon teal">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <div className="stat-content">
              <div className="stat-label">Resolved Clarification Amount</div>
              <div className="stat-value">{formatCurrency(amountSummary?.resolvedClarificationAmount)}</div>
              <div className="stat-meta teal">
                {amountSummary?.resolvedClarificationAmountPercentage != null
                  ? `${amountSummary.resolvedClarificationAmountPercentage.toFixed(1)}% resolved`
                  : '0% resolved'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show error message if API failed, but don't block dashboard rendering */}
      {error && (
        <div className="dashboard-error-notice" style={{ 
          padding: '16px', 
          marginBottom: '24px', 
          backgroundColor: '#fef3c7', 
          border: '1px solid #f59e0b', 
          borderRadius: '8px',
          color: '#92400e'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>⚠️</span>
            <span style={{ flex: 1 }}>
              <strong>Unable to load dashboard data:</strong> {error}
            </span>
            <button 
              onClick={fetchKPIData} 
              style={{
                padding: '6px 12px',
                backgroundColor: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Status Breakdown Section - Always show, even if empty */}
      <div className="status-section">
        <h2 className="section-title">Status Breakdown</h2>
        {statusBreakdown && statusBreakdown.length > 0 ? (
          <div className="status-grid">
            {statusBreakdown.map((status) => (
              <div key={status.status} className="status-card">
                <div className="status-header">
                  <div 
                    className="status-indicator" 
                    style={{ backgroundColor: STATUS_COLORS[status.status] }}
                  ></div>
                  <span className="status-name">{STATUS_LABELS[status.status] || status.status}</span>
                </div>
                <div className="status-stats">
                  <div className="status-count">{status.count}</div>
                  <div className="status-percentage">{status.percentage.toFixed(1)}%</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ 
            padding: '24px', 
            textAlign: 'center', 
            color: '#6b7280',
            backgroundColor: '#f9fafb',
            borderRadius: '8px'
          }}>
            <p>No status data available</p>
            {error && (
              <p style={{ fontSize: '14px', marginTop: '8px' }}>
                Status information could not be loaded due to insufficient permissions or API error.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Charts Section - Only show if we have status data */}
      {statusBreakdown && statusBreakdown.length > 0 && (
        <div className="charts-grid">
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Status Distribution</h3>
              <div className="chart-subtitle">Check count by status</div>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={prepareStatusChartData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {prepareStatusChartData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || '#8884d8'} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  formatter={(value, name, props) => [
                    `${value} checks (${props.payload.percentage.toFixed(1)}%)`,
                    name
                  ]}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px',
                    padding: '12px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="chart-legend">
              {prepareStatusChartData().map((entry, index) => (
                <div key={index} className="legend-item">
                  <div 
                    className="legend-color" 
                    style={{ backgroundColor: STATUS_COLORS[entry.status] || '#8884d8' }}
                  ></div>
                  <span className="legend-label">{entry.name}</span>
                  <span className="legend-percentage">{entry.percentage.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Unknown vs Known</h3>
              <div className="chart-subtitle">Count and percentage by type</div>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={prepareUnknownKnownChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  stroke="#6b7280"
                  tick={{ fontSize: 12 }}
                />
                <YAxis stroke="#6b7280" />
                <RechartsTooltip 
                  formatter={(value, name, props) => [
                    `${value} checks (${props.payload.percentage.toFixed(1)}%)`,
                    'Count'
                  ]}
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb', 
                    borderRadius: '8px',
                    padding: '12px'
                  }}
                />
                <Bar 
                  dataKey="count" 
                  radius={[8, 8, 0, 0]}
                >
                  {prepareUnknownKnownChartData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Check Wise Financials - Combined Table and Chart View */}
      {single?.checkWiseFinancials && single.checkWiseFinancials.length > 0 && (
        <div className="kpi-table-section status-breakdown-section">
          <div className="section-header-with-toggle">
            <h2 className="section-title">Financial Summary by Check Type</h2>
            <div className="view-toggle-container">
              <button
                className={`view-toggle-btn ${financialViewMode === 'table' ? 'active' : ''}`}
                onClick={() => setFinancialViewMode('table')}
                title="Table View"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
                </svg>
                <span>Table</span>
              </button>
              <button
                className={`view-toggle-btn ${financialViewMode === 'chart' ? 'active' : ''}`}
                onClick={() => setFinancialViewMode('chart')}
                title="Chart View"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"/>
                  <path d="M7 16l4-4 4 4 6-6"/>
                </svg>
                <span>Chart</span>
              </button>
            </div>
          </div>

          {/* Table View */}
          {financialViewMode === 'table' && (
            <div className="kpi-table-container">
              <table className="kpi-table">
                <thead>
                  <tr>
                    <th>Check Type</th>
                    <th>Total Checks</th>
                    <th>Total Amount</th>
                    <th>Posted Amount</th>
                    <th>Posted %</th>
                    <th>Other Amount</th>
                    <th>Other %</th>
                    <th>Remaining Amount</th>
                    <th>Remaining %</th>
                  </tr>
                </thead>
                <tbody>
                  {single.checkWiseFinancials
                    .filter(item => item.totalChecks > 0)
                    .map((item, index) => (
                      <tr key={index}>
                        <td className="check-type-cell">
                          <span className="check-type-badge">{item.checkType}</span>
                        </td>
                        <td className="number-cell">{item.totalChecks}</td>
                        <td className="currency-cell">{formatCurrency(item.totalCheckAmount)}</td>
                        <td className="currency-cell">{formatCurrency(item.totalPostedAmount)}</td>
                        <td className="percentage-cell">
                          <span className="percentage-value">{item.totalPostedPercentage.toFixed(2)}%</span>
                        </td>
                        <td className="currency-cell">{formatCurrency(item.totalOtherAmount)}</td>
                        <td className="percentage-cell">
                          <span className="percentage-value">{item.totalOtherAmountPercentage.toFixed(2)}%</span>
                        </td>
                        <td className="currency-cell">{formatCurrency(item.totalRemainingAmount)}</td>
                        <td className="percentage-cell">
                          <span className="percentage-value remaining">{item.totalRemainingAmountPercentage.toFixed(2)}%</span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Chart View */}
          {financialViewMode === 'chart' && filteredFinancialChartData.length > 0 && (
            <div className="combined-chart-view">
              <div className="combined-chart-card">
                <div className="combined-chart-header-inline">
                  <div className="combined-chart-subtitle">Percentage Distribution by Check Type</div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    marginBottom: '10px'
                  }}
                >
                  {[
                    { key: 'postedPercentage', label: 'Posted %', color: '#10b981' },
                    { key: 'otherPercentage', label: 'Other %', color: '#6366f1' },
                    { key: 'remainingPercentage', label: 'Remaining %', color: '#f59e0b' }
                  ].map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        setFinancialSeriesVisibility(prev => {
                          const next = { ...prev, [item.key]: !prev[item.key] };
                          const visibleCount = Object.values(next).filter(Boolean).length;
                          return visibleCount === 0 ? prev : next;
                        });
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        border: '1px solid #d1d5db',
                        borderRadius: '999px',
                        padding: '4px 10px',
                        background: financialSeriesVisibility[item.key] ? '#ffffff' : '#f3f4f6',
                        color: financialSeriesVisibility[item.key] ? '#111827' : '#6b7280',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600
                      }}
                      title={`${financialSeriesVisibility[item.key] ? 'Hide' : 'Show'} ${item.label}`}
                    >
                      <span
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          background: item.color,
                          opacity: financialSeriesVisibility[item.key] ? 1 : 0.3
                        }}
                      />
                      {item.label}
                    </button>
                  ))}
                </div>
                {hoveredFinancialData && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginBottom: '10px',
                      padding: '8px 10px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      background: '#f9fafb'
                    }}
                  >
                    <span style={{ fontWeight: 600, color: '#111827' }}>{hoveredFinancialData.checkType}</span>
                    <span style={{ color: '#059669', fontWeight: 500 }}>
                      Posted {hoveredFinancialData.postedPercentage.toFixed(2)}% ({formatCurrency(hoveredFinancialData.postedAmount)})
                    </span>
                    <span style={{ color: '#4f46e5', fontWeight: 500 }}>
                      Other {hoveredFinancialData.otherPercentage.toFixed(2)}% ({formatCurrency(hoveredFinancialData.otherAmount)})
                    </span>
                    <span style={{ color: '#d97706', fontWeight: 500 }}>
                      Remaining {hoveredFinancialData.remainingPercentage.toFixed(2)}% ({formatCurrency(hoveredFinancialData.remainingAmount)})
                    </span>
                  </div>
                )}
                <ResponsiveContainer width="100%" height={Math.max(360, filteredFinancialChartData.length * 28)}>
                  <BarChart
                    layout="vertical"
                    data={filteredFinancialChartData}
                    margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                    onMouseMove={(state) => {
                      const checkType = state?.activePayload?.[0]?.payload?.checkType;
                      if (checkType) setHoveredFinancialCheckType(checkType);
                    }}
                    onMouseLeave={() => setHoveredFinancialCheckType(null)}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      type="number"
                      stroke="#6b7280"
                      domain={[0, 100]}
                      tick={{ fontSize: 11, fill: '#374151' }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <YAxis
                      dataKey="checkType"
                      type="category"
                      stroke="#6b7280"
                      tick={renderFinancialCheckTypeTick}
                      width={90}
                    />
                    <RechartsTooltip
                      formatter={(value, name, props) => {
                        const keyMap = {
                          'Posted %': 'postedAmount',
                          'Other %': 'otherAmount',
                          'Remaining %': 'remainingAmount'
                        };
                        const amountKey = keyMap[name];
                        const amountValue = amountKey ? props?.payload?.[amountKey] : 0;
                        return [`${Number(value).toFixed(2)}% (${formatCurrency(amountValue)})`, name];
                      }}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '13px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    {financialSeriesVisibility.postedPercentage && (
                      <Bar dataKey="postedPercentage" name="Posted %" stackId="distribution" fill="#10b981">
                        {filteredFinancialChartData.map((entry, index) => (
                          <Cell
                            key={`posted-${entry.checkType}-${index}`}
                            fillOpacity={!hoveredFinancialCheckType || hoveredFinancialCheckType === entry.checkType ? 1 : 0.1}
                          />
                        ))}
                      </Bar>
                    )}
                    {financialSeriesVisibility.otherPercentage && (
                      <Bar dataKey="otherPercentage" name="Other %" stackId="distribution" fill="#6366f1">
                        {filteredFinancialChartData.map((entry, index) => (
                          <Cell
                            key={`other-${entry.checkType}-${index}`}
                            fillOpacity={!hoveredFinancialCheckType || hoveredFinancialCheckType === entry.checkType ? 1 : 0.1}
                          />
                        ))}
                      </Bar>
                    )}
                    {financialSeriesVisibility.remainingPercentage && (
                      <Bar dataKey="remainingPercentage" name="Remaining %" stackId="distribution" fill="#f59e0b">
                        {filteredFinancialChartData.map((entry, index) => (
                          <Cell
                            key={`remaining-${entry.checkType}-${index}`}
                            fillOpacity={!hoveredFinancialCheckType || hoveredFinancialCheckType === entry.checkType ? 1 : 0.1}
                          />
                        ))}
                      </Bar>
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="combined-chart-card" style={{ marginTop: '16px' }}>
                <div className="combined-chart-header-inline">
                  <div className="combined-chart-subtitle">Total Amount by Check Type</div>
                </div>
                <ResponsiveContainer width="100%" height={Math.max(360, filteredFinancialChartData.length * 28)}>
                  <BarChart
                    layout="vertical"
                    data={filteredFinancialChartData}
                    margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                    onMouseMove={(state) => {
                      const checkType = state?.activePayload?.[0]?.payload?.checkType;
                      if (checkType) setHoveredFinancialCheckType(checkType);
                    }}
                    onMouseLeave={() => setHoveredFinancialCheckType(null)}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      type="number"
                      stroke="#6b7280"
                      tick={{ fontSize: 11, fill: '#374151' }}
                      tickFormatter={(value) => formatCompactCurrency(value)}
                    />
                    <YAxis
                      dataKey="checkType"
                      type="category"
                      stroke="#6b7280"
                      tick={renderFinancialCheckTypeTick}
                      width={90}
                    />
                    <ReferenceLine x={0} stroke="#9ca3af" />
                    <RechartsTooltip
                      formatter={(value) => [formatCurrency(value), 'Total Amount']}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '13px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Bar dataKey="totalAmount" name="Total Amount" radius={[0, 4, 4, 0]}>
                      {filteredFinancialChartData.map((entry, index) => (
                        <Cell
                          key={`total-amount-${entry.checkType}-${index}`}
                          fill={entry.totalAmount < 0 ? '#ef4444' : '#3b82f6'}
                          fillOpacity={!hoveredFinancialCheckType || hoveredFinancialCheckType === entry.checkType ? 0.95 : 0.1}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div
                style={{
                  marginTop: '12px'
                }}
              >
                <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, marginBottom: '8px' }}>
                  Check Type Legend (click to show/hide)
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {financialChartData.map(item => {
                    const isVisible = financialCheckTypeVisibility[item.checkType] !== false;
                    return (
                      <button
                        key={`checktype-toggle-${item.checkType}`}
                        type="button"
                        onClick={() => {
                          setFinancialCheckTypeVisibility(prev => {
                            const currentVisibleCount = financialChartData.reduce((count, entry) => {
                              const visible = prev[entry.checkType] ?? true;
                              return count + (visible ? 1 : 0);
                            }, 0);
                            const isCurrentlyVisible = prev[item.checkType] ?? true;
                            if (isCurrentlyVisible && currentVisibleCount === 1) {
                              return prev;
                            }
                            return {
                              ...prev,
                              [item.checkType]: !isCurrentlyVisible
                            };
                          });
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          border: '1px solid #d1d5db',
                          borderRadius: '999px',
                          padding: '4px 10px',
                          background: isVisible ? '#ffffff' : '#f3f4f6',
                          color: isVisible ? '#111827' : '#9ca3af',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600
                        }}
                        title={`${isVisible ? 'Hide' : 'Show'} ${item.checkType}`}
                      >
                        <span
                          style={{
                            width: '9px',
                            height: '9px',
                            borderRadius: '50%',
                            background: '#3b82f6',
                            opacity: isVisible ? 1 : 0.25
                          }}
                        />
                        {item.checkType}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Check Wise Status Breakdown - Combined Table and Chart View */}
      {single?.checkWiseStatusBreakDown && single.checkWiseStatusBreakDown.length > 0 && (
        <div className="kpi-table-section status-breakdown-section">
          <div className="section-header-with-toggle">
            <h2 className="section-title">Status Breakdown by Check Type</h2>
            <div className="view-toggle-container">
              <button
                className={`view-toggle-btn ${statusViewMode === 'table' ? 'active' : ''}`}
                onClick={() => setStatusViewMode('table')}
                title="Table View"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
                </svg>
                <span>Table</span>
              </button>
              <button
                className={`view-toggle-btn ${statusViewMode === 'chart' ? 'active' : ''}`}
                onClick={() => setStatusViewMode('chart')}
                title="Chart View"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"/>
                  <path d="M7 16l4-4 4 4 6-6"/>
                </svg>
                <span>Chart</span>
              </button>
            </div>
          </div>

          {/* Table View */}
          {statusViewMode === 'table' && (
            <div className="kpi-table-container">
              <table className="kpi-table status-table">
                <thead>
                  <tr>
                    <th>Check Type</th>
                    <th>Total Checks</th>
                    <th>Not Started</th>
                    <th>In Progress</th>
                    <th>Completed</th>
                    <th>Under Clarifications</th>
                    <th>Over Posted</th>
                  </tr>
                </thead>
                <tbody>
                  {single.checkWiseStatusBreakDown
                    .filter(item => item.totalChecks > 0)
                    .map((item, index) => (
                      <tr key={index}>
                        <td className="check-type-cell">
                          <span className="check-type-badge">{item.checkType}</span>
                        </td>
                        <td className="number-cell">{item.totalChecks}</td>
                        <td className="status-count-cell">
                          <div className="status-count-wrapper">
                            <span className="status-count-value">{item.notStartedCount}</span>
                            <span className="status-count-percentage">({item.notStartedPercentage.toFixed(1)}%)</span>
                          </div>
                        </td>
                        <td className="status-count-cell">
                          <div className="status-count-wrapper">
                            <span className="status-count-value">{item.inProgressCount}</span>
                            <span className="status-count-percentage">({item.inProgressPercentage.toFixed(1)}%)</span>
                          </div>
                        </td>
                        <td className="status-count-cell">
                          <div className="status-count-wrapper">
                            <span className="status-count-value">{item.completedCount}</span>
                            <span className="status-count-percentage">({item.completedPercentage.toFixed(1)}%)</span>
                          </div>
                        </td>
                        <td className="status-count-cell">
                          <div className="status-count-wrapper">
                            <span className="status-count-value">{item.underClarificationsCount}</span>
                            <span className="status-count-percentage">({item.underClarificationsPercentage.toFixed(1)}%)</span>
                          </div>
                        </td>
                        <td className="status-count-cell">
                          <div className="status-count-wrapper">
                            <span className="status-count-value">{item.overPostedCount}</span>
                            <span className="status-count-percentage">({item.overPostedPercentage.toFixed(1)}%)</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Chart View */}
          {statusViewMode === 'chart' && prepareAllCheckTypesChartData().length > 0 && (
            <div className="combined-chart-view">
              <div className="combined-chart-header-inline">
                <div className="combined-chart-subtitle">Compare status distribution across all check types</div>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart 
                  data={prepareAllCheckTypesChartData()} 
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="checkType" 
                    stroke="#6b7280" 
                    tick={{ fontSize: 12, fill: '#374151' }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    stroke="#6b7280" 
                    tick={{ fontSize: 12, fill: '#374151' }}
                    label={{ value: 'Number of Checks', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280' } }}
                  />
                  <RechartsTooltip 
                    formatter={(value, name) => [
                      `${value} checks`,
                      name
                    ]}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '13px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                  />
                  <Bar 
                    dataKey="Not Started" 
                    stackId="status"
                    fill={STATUS_COLORS['NOT_STARTED']}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar 
                    dataKey="In Progress" 
                    stackId="status"
                    fill={STATUS_COLORS['IN_PROGRESS']}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar 
                    dataKey="Completed" 
                    stackId="status"
                    fill={STATUS_COLORS['COMPLETED']}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar 
                    dataKey="Clarifications" 
                    stackId="status"
                    fill={STATUS_COLORS['UNDER_CLARIFICATIONS']}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar 
                    dataKey="Over Posted" 
                    stackId="status"
                    fill={STATUS_COLORS['OVER_POSTED']}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
              <div className="combined-chart-legend">
                <div className="legend-title">Status Legend:</div>
                <div className="legend-items-container">
                  <div className="combined-legend-item">
                    <div 
                      className="combined-legend-color" 
                      style={{ backgroundColor: STATUS_COLORS['NOT_STARTED'] }}
                    ></div>
                    <span className="combined-legend-label">Not Started</span>
                  </div>
                  <div className="combined-legend-item">
                    <div 
                      className="combined-legend-color" 
                      style={{ backgroundColor: STATUS_COLORS['IN_PROGRESS'] }}
                    ></div>
                    <span className="combined-legend-label">In Progress</span>
                  </div>
                  <div className="combined-legend-item">
                    <div 
                      className="combined-legend-color" 
                      style={{ backgroundColor: STATUS_COLORS['COMPLETED'] }}
                    ></div>
                    <span className="combined-legend-label">Completed</span>
                  </div>
                  <div className="combined-legend-item">
                    <div 
                      className="combined-legend-color" 
                      style={{ backgroundColor: STATUS_COLORS['UNDER_CLARIFICATIONS'] }}
                    ></div>
                    <span className="combined-legend-label">Under Clarifications</span>
                  </div>
                  <div className="combined-legend-item">
                    <div 
                      className="combined-legend-color" 
                      style={{ backgroundColor: STATUS_COLORS['OVER_POSTED'] }}
                    ></div>
                    <span className="combined-legend-label">Over Posted</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}


      {/* Filter Sidebar */}
      {showFilterSidebar && (
        <>
          <div className="filter-sidebar-overlay" onClick={() => setShowFilterSidebar(false)}></div>
          <div className="filter-sidebar">
            <div className="filter-sidebar-header">
              <h2>Custom Filters</h2>
              <button 
                className="filter-sidebar-close"
                onClick={() => setShowFilterSidebar(false)}
                title="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="filter-sidebar-content">
              {/* Saved Filters Section */}
              <div className="dashboard-saved-filters-section">
                <div className="dashboard-saved-filters-header">
                  <BookmarkPlus size={14} />
                  <span>Saved Filters</span>
                </div>
                <div className="dashboard-saved-filters-select-wrapper">
                  <select
                    className="dashboard-saved-filters-select"
                    value={selectedSavedFilterId}
                    onChange={e => handleLoadDashboardSavedFilter(e.target.value)}
                    disabled={isLoadingSavedFilters}
                  >
                    <option value="">
                      {isLoadingSavedFilters ? 'Loading...' : savedFilters.length === 0 ? 'No saved filters yet' : '— Select a saved filter —'}
                    </option>
                    {savedFilters.map(sf => (
                      <option key={sf.id} value={sf.id}>{sf.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={13} className="dashboard-saved-filters-chevron" />
                </div>
                {selectedSavedFilterId && (
                  <p className="dashboard-saved-filters-hint">Filter loaded — adjust values below then click Apply.</p>
                )}
              </div>

              {/* Status Filter */}
              <div className="sidebar-filter-group">
                {renderModeLabel('statuses', 'Status')}
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={[
                        { value: 'NOT_STARTED', label: 'Not Started' },
                        { value: 'IN_PROGRESS', label: 'In Progress' },
                        { value: 'UNDER_CLARIFICATIONS', label: 'Under Clarifications' },
                        { value: 'COMPLETED', label: 'Completed' },
                        { value: 'OVER_POSTED', label: 'Over Posted' }
                      ].filter(opt => !getSelectedValues('statuses').includes(opt.value))}
                      value=""
                      onChange={(value) => {
                        if (value) handleAddFilterValue('statuses', value);
                      }}
                      placeholder="Select Status"
                      maxVisibleItems={5}
                    />
                  </div>
                  {getSelectedValues('statuses').length > 0 && (
                    <div className="selected-practices">
                      {getSelectedValues('statuses').map((status) => {
                        const statusLabels = {
                          'NOT_STARTED': 'Not Started',
                          'IN_PROGRESS': 'In Progress',
                          'UNDER_CLARIFICATIONS': 'Under Clarifications',
                          'COMPLETED': 'Completed',
                          'OVER_POSTED': 'Over Posted'
                        };
                        return (
                          <div key={status} className="selected-practice-chip">
                            <span>{isExcludeMode('statuses') ? `Not ${statusLabels[status] || status}` : statusLabels[status] || status}</span>
                            <button
                              onClick={() => handleRemoveFilterValue('statuses', status)}
                              className="chip-remove-btn"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Practice Filter */}
              <div className="sidebar-filter-group">
                {renderModeLabel('practiceCodes', 'Practice')}
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={practices
                        .filter(p => !getSelectedValues('practiceCodes').includes(p.code))
                        .map(p => ({ value: p.code, label: `${p.code} - ${p.name}` }))}
                      value={selectedPracticeValue}
                      onChange={(value) => {
                        if (value) {
                          handleAddFilterValue('practiceCodes', value);
                          setSelectedPracticeValue('');
                        }
                      }}
                      placeholder="Select Practice"
                      maxVisibleItems={5}
                    />
                  </div>
                  {getSelectedValues('practiceCodes').length > 0 && (
                    <div className="selected-practices">
                      {getSelectedValues('practiceCodes').map((practiceCode) => (
                        <div key={practiceCode} className="selected-practice-chip">
                          <span>{isExcludeMode('practiceCodes') ? `Not ${practices.find(p => p.code === practiceCode)?.name || practiceCode}` : (practices.find(p => p.code === practiceCode)?.name || practiceCode)}</span>
                          <button
                            onClick={() => handleRemoveFilterValue('practiceCodes', practiceCode)}
                            className="chip-remove-btn"
                          >
                            <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Location Filter */}
              <div className="sidebar-filter-group">
                {renderModeLabel('locationCodes', 'Location')}
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={locations
                        .filter(l => l.isActive !== false)
                        .filter(l => !getSelectedValues('locationCodes').includes(l.code))
                        .map(location => ({
                          value: location.code,
                          label: location.name || location.code
                        }))}
                      value=""
                      onChange={(value) => {
                        if (value) handleAddFilterValue('locationCodes', value);
                      }}
                      placeholder="Select Location"
                      disabled={!((localFilters.practiceCodes && localFilters.practiceCodes.length > 0) || (localFilters.excludePracticeCodes && localFilters.excludePracticeCodes.length > 0))}
                      maxVisibleItems={5}
                    />
                  </div>
                  {getSelectedValues('locationCodes').length > 0 && (
                    <div className="selected-practices">
                      {getSelectedValues('locationCodes').map((locationCode) => {
                        const location = locations.find(l => l.code === locationCode);
                        const label = location?.name || locationCode;
                        return (
                          <div key={locationCode} className="selected-practice-chip">
                            <span>{isExcludeMode('locationCodes') ? `Not ${label}` : label}</span>
                            <button
                              onClick={() => handleRemoveFilterValue('locationCodes', locationCode)}
                              className="chip-remove-btn"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Assignee Filter */}
              <div className="sidebar-filter-group">
                {renderModeLabel('assigneeIds', 'Assignee Name')}
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={users
                        .filter(user => !getSelectedValues('assigneeIds').includes(user.userId || user.id))
                        .map(user => ({
                          value: user.userId || user.id,
                          label: user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}` 
                            : user.email || 'Unknown User'
                        }))}
                      value=""
                      onChange={(value) => {
                        if (value) handleAddFilterValue('assigneeIds', value);
                      }}
                      placeholder="Select Assignee"
                      maxVisibleItems={5}
                    />
                  </div>
                  {getSelectedValues('assigneeIds').length > 0 && (
                    <div className="selected-practices">
                      {getSelectedValues('assigneeIds').map((assigneeId) => {
                        const user = users.find(u => (u.userId || u.id) === assigneeId);
                        const label = user && user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : user?.email || assigneeId;
                        return (
                          <div key={assigneeId} className="selected-practice-chip">
                            <span>{isExcludeMode('assigneeIds') ? `Not ${label}` : label}</span>
                            <button
                              onClick={() => handleRemoveFilterValue('assigneeIds', assigneeId)}
                              className="chip-remove-btn"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Reporter Filter */}
              <div className="sidebar-filter-group">
                {renderModeLabel('reporterIds', 'Reporter Name')}
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={users
                        .filter(user => !getSelectedValues('reporterIds').includes(user.userId || user.id))
                        .map(user => ({
                          value: user.userId || user.id,
                          label: user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}` 
                            : user.email || 'Unknown User'
                        }))}
                      value=""
                      onChange={(value) => {
                        if (value) handleAddFilterValue('reporterIds', value);
                      }}
                      placeholder="Select Reporter"
                      maxVisibleItems={5}
                    />
                  </div>
                  {getSelectedValues('reporterIds').length > 0 && (
                    <div className="selected-practices">
                      {getSelectedValues('reporterIds').map((reporterId) => {
                        const user = users.find(u => (u.userId || u.id) === reporterId);
                        const label = user && user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : user?.email || reporterId;
                        return (
                          <div key={reporterId} className="selected-practice-chip">
                            <span>{isExcludeMode('reporterIds') ? `Not ${label}` : label}</span>
                            <button
                              onClick={() => handleRemoveFilterValue('reporterIds', reporterId)}
                              className="chip-remove-btn"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Check Number Filter */}
              <div className="sidebar-filter-group">
                <label>Check Number</label>
                <input
                  type="text"
                  value={localFilters.checkNumber || ''}
                  onChange={(e) => handleFilterChange('checkNumber', e.target.value)}
                  placeholder="Enter check number (e.g., ch* or ch)"
                  className="sidebar-filter-input"
                />
              </div>

              {/* Check Type Filter */}
              <div className="sidebar-filter-group">
                {renderModeLabel('checkTypes', 'Check Type')}
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={checkTypes.map(ct => ({
                        value: ct.code,
                        label: ct.name
                      })).filter(opt => !getSelectedValues('checkTypes').includes(opt.value))}
                      value=""
                      onChange={(value) => {
                        if (value) handleAddFilterValue('checkTypes', value);
                      }}
                      placeholder="Select Check Type"
                      maxVisibleItems={5}
                      disabled={checkTypesLoading}
                    />
                  </div>
                  {getSelectedValues('checkTypes').length > 0 && (
                    <div className="selected-practices">
                      {getSelectedValues('checkTypes').map((checkType) => {
                        const checkTypeItem = checkTypes.find(ct => ct.code === checkType);
                        const label = checkTypeItem?.name || checkType;
                        return (
                          <div key={checkType} className="selected-practice-chip">
                            <span>{isExcludeMode('checkTypes') ? `Not ${label}` : label}</span>
                            <button
                              onClick={() => handleRemoveFilterValue('checkTypes', checkType)}
                              className="chip-remove-btn"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Workflow Status Filter */}
              <div className="sidebar-filter-group">
                {renderModeLabel('workflowStatuses', 'Workflow Status')}
                <div className="multi-select-wrapper">
                  <div className="multi-select-input">
                    <SearchableDropdown
                      options={workflowStatuses.map(ws => ({
                        value: ws.code,
                        label: ws.name
                      })).filter(opt => !getSelectedValues('workflowStatuses').includes(opt.value))}
                      value=""
                      onChange={(value) => {
                        if (value) handleAddFilterValue('workflowStatuses', value);
                      }}
                      placeholder="Select Workflow Status"
                      maxVisibleItems={5}
                      disabled={workflowStatusesLoading}
                    />
                  </div>
                  {getSelectedValues('workflowStatuses').length > 0 && (
                    <div className="selected-practices">
                      {getSelectedValues('workflowStatuses').map((workflowStatus) => {
                        const workflowStatusItem = workflowStatuses.find(ws => ws.code === workflowStatus);
                        const label = workflowStatusItem?.name || workflowStatus;
                        return (
                          <div key={workflowStatus} className="selected-practice-chip">
                            <span>{isExcludeMode('workflowStatuses') ? `Not ${label}` : label}</span>
                            <button
                              onClick={() => handleRemoveFilterValue('workflowStatuses', workflowStatus)}
                              className="chip-remove-btn"
                            >
                              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Payer Filter */}
              <div className="sidebar-filter-group">
                <label>Payer</label>
                <input
                  type="text"
                  value={localFilters.payer || ''}
                  onChange={(e) => handleFilterChange('payer', e.target.value)}
                  placeholder="Enter payer name (e.g., *blue* or blue)"
                  className="sidebar-filter-input"
                />
              </div>

              {/* Unknown Filter */}
              <div className="sidebar-filter-group">
                <label>Unknown</label>
                <SearchableDropdown
                  options={[
                    { value: '', label: 'All' },
                    { value: 'false', label: 'False' },
                    { value: 'true', label: 'True' }
                  ]}
                  value={localFilters.unknown === undefined ? '' : (localFilters.unknown === true ? 'true' : 'false')}
                  onChange={(value) => {
                    if (value === '') {
                      handleFilterChange('unknown', undefined);
                    } else {
                      handleFilterChange('unknown', value === 'true');
                    }
                  }}
                  placeholder="All"
                  maxVisibleItems={3}
                />
              </div>

              {/* Date Filters Section */}
              <div className="sidebar-filter-group">
                <label>Deposit Date From</label>
                <USDateInput
                  name="depositDateFrom"
                  value={localFilters.depositDateFrom || ''}
                  onChange={(e) => handleFilterChange('depositDateFrom', e.target.value)}
                  className="sidebar-filter-input"
                />
              </div>
              
              <div className="sidebar-filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Deposit Date To{localFilters.depositDateFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={depositDateToRef}
                  name="depositDateTo"
                  value={localFilters.depositDateTo || ''}
                  onChange={(e) => handleFilterChange('depositDateTo', e.target.value)}
                  min={localFilters.depositDateFrom || undefined}
                  max={calculateMaxDate(localFilters.depositDateFrom)}
                  className="sidebar-filter-input"
                  disabled={!localFilters.depositDateFrom || localFilters.depositDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Deposit Date.')}
                />
              </div>

              <div className="sidebar-filter-group">
                <label>Received Date From</label>
                <USDateInput
                  name="receivedDateFrom"
                  value={localFilters.receivedDateFrom || ''}
                  onChange={(e) => handleFilterChange('receivedDateFrom', e.target.value)}
                  className="sidebar-filter-input"
                />
              </div>
              
              <div className="sidebar-filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Received Date To{localFilters.receivedDateFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={receivedDateToRef}
                  name="receivedDateTo"
                  value={localFilters.receivedDateTo || ''}
                  onChange={(e) => handleFilterChange('receivedDateTo', e.target.value)}
                  min={localFilters.receivedDateFrom || undefined}
                  max={calculateMaxDate(localFilters.receivedDateFrom)}
                  className="sidebar-filter-input"
                  disabled={!localFilters.receivedDateFrom || localFilters.receivedDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Received Date.')}
                />
              </div>

              <div className="sidebar-filter-group">
                <label>Completed Date From</label>
                <USDateInput
                  name="completedDateFrom"
                  value={localFilters.completedDateFrom || ''}
                  onChange={(e) => handleFilterChange('completedDateFrom', e.target.value)}
                  className="sidebar-filter-input"
                />
              </div>
              
              <div className="sidebar-filter-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Completed Date To{localFilters.completedDateFrom ? <span className="required">*</span> : ''}
                  <Tooltip text="Select start date to enable" position="top">
                    <Info 
                      size={14} 
                      style={{ 
                        color: '#6b7280', 
                        cursor: 'pointer',
                        transition: 'color 0.2s',
                        strokeWidth: 2.5,
                        flexShrink: 0,
                        marginBottom: '2px'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0d9488'}
                      onMouseLeave={(e) => e.target.style.color = '#6b7280'}
                    />
                  </Tooltip>
                </label>
                <USDateInput
                  ref={completedDateToRef}
                  name="completedDateTo"
                  value={localFilters.completedDateTo || ''}
                  onChange={(e) => handleFilterChange('completedDateTo', e.target.value)}
                  min={localFilters.completedDateFrom || undefined}
                  max={calculateMaxDate(localFilters.completedDateFrom)}
                  className="sidebar-filter-input"
                  disabled={!localFilters.completedDateFrom || localFilters.completedDateFrom.trim() === ''}
                  onDisabledClick={() => showError('First you need to select start date for Completed Date.')}
                />
              </div>
            </div>
            <div className="filter-sidebar-footer">
              <button onClick={() => {
                clearFilters();
                setShowFilterSidebar(false);
              }} className="sidebar-clear-button">
                Clear All
              </button>
              <button
                className="sidebar-save-filter-button"
                onClick={() => { setSaveFilterName(''); setSaveFilterOrder(''); setShowSaveFilterModal(true); }}
              >
                <BookmarkPlus size={14} />
                Save
              </button>
              <button onClick={() => {
                const success = applyFilters();
                if (success) {
                  setShowFilterSidebar(false);
                }
              }} className="sidebar-apply-button">
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}

      {/* Save Filter Name Modal (ChecksDashboard) */}
      {showSaveFilterModal && (
        <div className="save-filter-modal-overlay" onClick={() => !isSavingFilter && setShowSaveFilterModal(false)}>
          <div className="save-filter-modal" onClick={e => e.stopPropagation()}>
            <div className="save-filter-modal-header">
              <BookmarkPlus size={18} />
              <h3>Save Current Filter</h3>
              <button className="save-filter-modal-close" onClick={() => setShowSaveFilterModal(false)} disabled={isSavingFilter}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="save-filter-modal-body">
              <label className="save-filter-label">Filter Name <span className="save-filter-required">*</span></label>
              <input
                type="text"
                className="save-filter-input"
                placeholder="e.g. Dashboard – Q1 2025"
                value={saveFilterName}
                onChange={e => setSaveFilterName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveDashboardFilter(); if (e.key === 'Escape') setShowSaveFilterModal(false); }}
                autoFocus
                maxLength={100}
                disabled={isSavingFilter}
              />
              <label className="save-filter-label" style={{ marginTop: '14px' }}>Sort Order <span className="save-filter-hint-text">(optional)</span></label>
              <input
                type="number"
                className="save-filter-input"
                placeholder="e.g. 1, 2, 3…"
                value={saveFilterOrder}
                onChange={e => setSaveFilterOrder(e.target.value)}
                min={0}
                disabled={isSavingFilter}
              />
            </div>
            <div className="save-filter-modal-footer">
              <button className="save-filter-btn-cancel" onClick={() => setShowSaveFilterModal(false)} disabled={isSavingFilter}>
                Cancel
              </button>
              <button className="save-filter-btn-confirm" onClick={handleSaveDashboardFilter} disabled={isSavingFilter || !saveFilterName.trim()}>
                {isSavingFilter ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecksDashboard;
